import type { AppState, Machine, Alert, Alarm, CurrentUser } from './types';
import { openControllerAlarmDetailModal, openAlertDetailModal } from './alarm-manual';

/* ═══ STATE ═════════════════════════════════════════════════════ */
export const state: AppState = {
    machines: [],
    machineHours: JSON.parse(localStorage.getItem('machineHours') || '{}'),
    machineStatus: JSON.parse(localStorage.getItem('machineStatus') || '{}'),
    hoursHistory: JSON.parse(localStorage.getItem('hoursHistory') || '{}'),
    ackedAlarms: JSON.parse(localStorage.getItem('ackedAlarms') || '[]'),
    allAlerts: [],
    partsPage: 1,
    errorsPage: 1,
    sensorHistory: JSON.parse(localStorage.getItem('sensorHistory') || '{}'),
    consumptionHistory: {},
    hourlyAvg: {},
    currentUser: { username: null, role: 'operator' },
};

/* ═══ HELPERS ══════════════════════════════════════════════════ */
export async function api(url: string, opts: RequestInit = {}): Promise<any> {
    const res = await fetch(url, { credentials: 'include', ...opts });
    const text = await res.text();
    try {
        const data = text ? JSON.parse(text) : null;
        if (!res.ok && data && data.error) throw new Error(data.error);
        if (!res.ok) throw new Error(`Eroare ${res.status}`);
        return data;
    } catch (e) {
        if (e instanceof SyntaxError && text.includes('<!doctype'))
            throw new Error('Endpoint indisponibil. Reporniți serviciul: systemctl restart fabrica-mentenanta');
        throw e;
    }
}

// 11 oct 2026: implicit HTMLElement (toate elementele aplicației sunt HTML) — `.style`, `.dataset`, `.focus()` erau
// 150 de erori de tip „nu există pe Element”; pentru câmpuri: `$<HTMLInputElement>('#x')`. Aceeași funcție la rulare.
export function $<T extends Element = HTMLElement>(sel: string, ctx: ParentNode = document): T | null { return ctx.querySelector<T>(sel); }
export function $$<T extends Element = HTMLElement>(sel: string, ctx: ParentNode = document): NodeListOf<T> { return ctx.querySelectorAll<T>(sel); }

export function toast(msg: string, type: string = 'success'): void {
    const el = document.createElement('div');
    el.className = `toast ${type}`;
    el.textContent = msg;
    document.body.appendChild(el);
    setTimeout(() => el.remove(), type === 'info' ? 3500 : 3000);
}

export function customConfirm(message: string, title?: string): Promise<boolean> {
    return new Promise((resolve) => {
        const overlay = document.createElement('div');
        overlay.style.cssText = 'position:fixed;inset:0;z-index:9999;background:rgba(0,0,0,.7);display:flex;align-items:center;justify-content:center;animation:fadeIn .15s ease';
        const box = document.createElement('div');
        // 5 aug 2026 — audit mobil, punctul 6: fără max-height, un mesaj lung
        // împingea butoanele Anulează/OK sub marginea ecranului pe telefon
        // (și în peisaj pe tabletă), fără nicio cale de a ajunge la ele.
        box.style.cssText = 'background:var(--surface,#1e293b);border:1px solid var(--border,#334155);border-radius:16px;padding:28px 32px;max-width:460px;width:90%;max-height:85dvh;overflow-y:auto;box-shadow:0 20px 60px rgba(0,0,0,.5)';
        const titleText = title || 'Confirmare';
        box.innerHTML = `
            <div style="font-size:1.1rem;font-weight:700;color:var(--text,#fff);margin-bottom:16px">${titleText}</div>
            <div style="font-size:.95rem;line-height:1.6;color:var(--text-muted,#94a3b8);margin-bottom:24px;white-space:pre-line">${message}</div>
            <div style="display:flex;gap:12px;justify-content:flex-end">
                <button id="_cc_cancel" style="min-height:44px;padding:10px 24px;font-size:.95rem;font-weight:600;border-radius:10px;border:1px solid var(--border,#334155);background:rgba(255,255,255,.06);color:var(--text,#fff);cursor:pointer">Anulează</button>
                <button id="_cc_ok" style="min-height:44px;padding:10px 28px;font-size:.95rem;font-weight:700;border-radius:10px;border:none;background:#00e676;color:#000;cursor:pointer">OK</button>
            </div>`;
        overlay.appendChild(box);
        document.body.appendChild(overlay);
        const cleanup = (result: boolean) => { overlay.remove(); resolve(result); };
        box.querySelector('#_cc_ok')!.addEventListener('click', () => cleanup(true));
        box.querySelector('#_cc_cancel')!.addEventListener('click', () => cleanup(false));
        overlay.addEventListener('click', (e) => { if (e.target === overlay) cleanup(false); });
    });
}

export function customPrompt(message: string, defaultValue?: string, title?: string): Promise<string | null> {
    return new Promise((resolve) => {
        const overlay = document.createElement('div');
        overlay.style.cssText = 'position:fixed;inset:0;z-index:9999;background:rgba(0,0,0,.7);display:flex;align-items:center;justify-content:center;animation:fadeIn .15s ease';
        const box = document.createElement('div');
        // 5 aug 2026 — audit mobil, punctul 6 (vezi nota de la customConfirm).
        box.style.cssText = 'background:var(--surface,#1e293b);border:1px solid var(--border,#334155);border-radius:16px;padding:28px 32px;max-width:520px;width:90%;max-height:85dvh;overflow-y:auto;box-shadow:0 20px 60px rgba(0,0,0,.5)';
        const titleText = title || 'Introducere date';
        const inputEl = `<textarea id="_cp_input" rows="3" style="width:100%;padding:10px 14px;font-size:.95rem;border-radius:10px;border:1px solid var(--border,#334155);background:rgba(255,255,255,.08);color:var(--text,#fff);resize:vertical;font-family:inherit;min-height:60px">${defaultValue || ''}</textarea>`;
        box.innerHTML = `
            <div style="font-size:1.1rem;font-weight:700;color:var(--text,#fff);margin-bottom:16px">${titleText}</div>
            <div style="font-size:.95rem;line-height:1.6;color:var(--text-muted,#94a3b8);margin-bottom:16px;white-space:pre-line">${message}</div>
            ${inputEl}
            <div style="display:flex;gap:12px;justify-content:flex-end;margin-top:20px">
                <button id="_cp_cancel" style="padding:10px 24px;font-size:.95rem;font-weight:600;border-radius:10px;border:1px solid var(--border,#334155);background:rgba(255,255,255,.06);color:var(--text,#fff);cursor:pointer">Anulează</button>
                <button id="_cp_ok" style="padding:10px 28px;font-size:.95rem;font-weight:700;border-radius:10px;border:none;background:#00e676;color:#000;cursor:pointer">OK</button>
            </div>`;
        overlay.appendChild(box);
        document.body.appendChild(overlay);
        const inp = box.querySelector('#_cp_input') as HTMLInputElement | HTMLTextAreaElement;
        setTimeout(() => inp.focus(), 50);
        const cleanup = (val: string | null) => { overlay.remove(); resolve(val); };
        box.querySelector('#_cp_ok')!.addEventListener('click', () => cleanup(inp.value));
        box.querySelector('#_cp_cancel')!.addEventListener('click', () => cleanup(null));
        inp.addEventListener('keydown', (e: Event) => { const k = e as KeyboardEvent; if (k.key === 'Enter' && k.ctrlKey) cleanup(inp.value); });
    });
}

export function customSelect(message: string, options: string[], title?: string): Promise<number | null> {
    return new Promise((resolve) => {
        const overlay = document.createElement('div');
        overlay.style.cssText = 'position:fixed;inset:0;z-index:9999;background:rgba(0,0,0,.7);display:flex;align-items:center;justify-content:center;animation:fadeIn .15s ease';
        const box = document.createElement('div');
        box.style.cssText = 'background:var(--surface,#1e293b);border:1px solid var(--border,#334155);border-radius:16px;padding:28px 32px;max-width:560px;width:90%;box-shadow:0 20px 60px rgba(0,0,0,.5);max-height:80vh;max-height:80dvh;overflow-y:auto';
        const titleText = title || 'Selectare';
        const optHtml = options.map((o, i) =>
            `<button class="_cs_opt" data-idx="${i}" style="display:block;width:100%;text-align:left;padding:10px 14px;margin-bottom:6px;font-size:.9rem;border-radius:8px;border:1px solid var(--border,#334155);background:rgba(255,255,255,.05);color:var(--text,#fff);cursor:pointer;transition:background .15s">${i + 1}. ${o}</button>`
        ).join('');
        box.innerHTML = `
            <div style="font-size:1.1rem;font-weight:700;color:var(--text,#fff);margin-bottom:16px">${titleText}</div>
            <div style="font-size:.95rem;line-height:1.6;color:var(--text-muted,#94a3b8);margin-bottom:16px;white-space:pre-line">${message}</div>
            ${optHtml}
            <div style="display:flex;gap:12px;justify-content:flex-end;margin-top:16px">
                <button id="_cs_cancel" style="padding:10px 24px;font-size:.95rem;font-weight:600;border-radius:10px;border:1px solid var(--border,#334155);background:rgba(255,255,255,.06);color:var(--text,#fff);cursor:pointer">Anulează</button>
            </div>`;
        overlay.appendChild(box);
        document.body.appendChild(overlay);
        const cleanup = (val: number | null) => { overlay.remove(); resolve(val); };
        box.querySelectorAll('._cs_opt').forEach(btn => {
            btn.addEventListener('click', () => cleanup(parseInt((btn as HTMLElement).dataset.idx || '0', 10)));
            btn.addEventListener('mouseenter', () => (btn as HTMLElement).style.background = 'rgba(0,230,118,.15)');
            btn.addEventListener('mouseleave', () => (btn as HTMLElement).style.background = 'rgba(255,255,255,.05)');
        });
        box.querySelector('#_cs_cancel')!.addEventListener('click', () => cleanup(null));
    });
}

/* ═══ Android / tastă Înapoi — nu ieși din app; dublu-Înapoi pentru ieșire ═══ */
(window as any)._maintHistorySkip = 0;
(window as any)._maintLastExitTap = 0;

export function _maintIsLayerVisible(el: HTMLElement | null): boolean {
    if (!el) return false;
    if (el.classList && el.classList.contains('hidden')) return false;
    const st = getComputedStyle(el);
    return st.display !== 'none' && st.visibility !== 'hidden' && parseFloat(st.opacity || '1') > 0;
}

export function _maintCloseTopLayer(): number {
    const $id = (id: string): HTMLElement | null => document.getElementById(id);
    // manualul deschis în aplicație (manual-overlay.ts) stă deasupra tuturor — primul care se închide
    if ((window as any).__fmInchideManual?.()) return 1;
    const rm = (id: string): boolean => {
        const e = $id(id);
        if (e && e.parentNode) {
            e.remove();
            return true;
        }
        return false;
    };

    if (rm('overlay-3d-upload')) return 1;

    const stockN = $id('stock-available-notification');
    if (stockN && _maintIsLayerVisible(stockN)) {
        stockN.remove();
        return 1;
    }

    const layoutPv = $id('layout-photo-viewer');
    if (layoutPv) {
        const dsp = getComputedStyle(layoutPv).display;
        if (dsp !== 'none' && dsp !== '') {
            layoutPv.classList.add('hidden');
            layoutPv.style.display = 'none';
            return 1;
        }
    }

    const lb = $id('lightbox-overlay');
    if (lb && getComputedStyle(lb).display !== 'none') {
        lb.style.display = 'none';
        return 1;
    }

    if (rm('wf-detail-overlay')) return 1;
    if (rm('log-detail-overlay')) return 1;
    if (rm('mytask-postpone-overlay')) return 1;
    if (rm('cal-assign-overlay')) return 1;
    if (rm('proc-new-overlay')) return 1;

    const procSkip = new Set(['revision-detail-overlay']);
    const procs = [...document.querySelectorAll('.proc-overlay')];
    for (let i = procs.length - 1; i >= 0; i--) {
        const p = procs[i];
        if (procSkip.has(p.id)) continue;
        if (_maintIsLayerVisible(p as HTMLElement)) {
            p.remove();
            return 1;
        }
    }

    const rev = $id('revision-detail-overlay');
    if (rev && !rev.classList.contains('hidden')) {
        const ok = (window as any).closeRevisionDetailView?.(false);
        if (!ok) {
            try {
                history.pushState({ _maintPad: 1 }, '', location.href);
            } catch (e) {}
            return 2;
        }
        return 1;
    }

    const pq = $id('pq-overlay');
    if (pq && !pq.classList.contains('hidden')) {
        pq.classList.add('hidden');
        return 1;
    }
    const ll = $id('ll-overlay');
    if (ll && !ll.classList.contains('hidden')) {
        ll.classList.add('hidden');
        return 1;
    }
    const mp = $id('machine-panel-overlay');
    if (mp && !mp.classList.contains('hidden')) {
        mp.classList.add('hidden');
        return 1;
    }
    const det = $id('detail-overlay');
    if (det && !det.classList.contains('hidden')) {
        det.classList.add('hidden');
        return 1;
    }

    const chatSetup = $id('chat-api-setup');
    if (chatSetup && !chatSetup.classList.contains('hidden')) {
        chatSetup.classList.add('hidden');
        return 1;
    }

    const modals = ['modal-overlay', 'modal-login', 'modal-users', 'modal-add-line', 'modal-add-equip', 'modal-edit-equip', 'modal-shared-sensors', 'modal-counters', 'modal-collector'];
    for (const mid of modals) {
        const m = $id(mid);
        if (m && !m.classList.contains('hidden')) {
            m.classList.add('hidden');
            return 1;
        }
    }

    // Panoul de alarme e ascuns cu `hidden` (panou + văl), nu doar cu `.open` — a scoate doar
    // clasa lăsa panoul și vălul pe ecran, peste tot, iar nicio atingere nu mai ajungea la pagină
    // (30 sep 2026, găsit de turul ferestrelor). Același drum ca ✕ și clicul pe văl.
    const alarm = $id('alarm-panel');
    if (alarm && !alarm.hidden) {
        inchideAlarme();
        return 1;
    }

    const sidebar = $id('sidebar');
    if (sidebar && sidebar.classList.contains('open')) {
        sidebar.classList.remove('open');
        const bd = $id('sidebar-backdrop');
        if (bd) bd.classList.remove('visible');
        return 1;
    }

    // panoul clopoțelului (alertele AI proactive) — nu e pe tot ecranul, deci îl numim aici
    const pa = $id('proactive-alerts-panel');
    if (pa && !pa.classList.contains('hidden')) {
        ($id('proactive-alerts-btn') as HTMLElement | null)?.click();   // butonul îl comută: deschis → închis
        return 1;
    }

    // Orice altă fereastră peste tot ecranul — dialogurile create la cerere (Am reparat, amânare,
    // predare, piesă, cronologie…: peste 25 în cod) nu erau înscrise aici, iar „înapoi" arăta
    // „Apasă din nou…" cu dialogul încă deschis (30 sep 2026, turul ferestrelor). Închidem cea mai
    // de sus prin butonul ei ✕ (ca să ruleze codul ei de închidere); fără buton, o scoatem.
    // Nu se ating straturile de fundal (pânze, elemente fără atingeri) și bara / meniul aplicației.
    const peste = (Array.from(document.body.children) as HTMLElement[]).reverse().find(h => {
        if (['SCRIPT', 'STYLE', 'CANVAS', 'VIDEO', 'LINK'].includes(h.tagName)) return false;
        if (h.matches('#app-wrapper, #d-bara-jos, #sidebar, #sidebar-backdrop, #auth-gate, #alarm-overlay, #alarm-panel, #lp-back-pill')) return false;
        if (h.hidden || h.classList.contains('hidden')) return false;
        const cs = getComputedStyle(h);
        if (cs.position !== 'fixed' || cs.display === 'none' || cs.visibility === 'hidden' || cs.pointerEvents === 'none') return false;
        const r = h.getBoundingClientRect();
        return r.width >= window.innerWidth * 0.9 && r.height >= window.innerHeight * 0.9;
    });
    if (peste) {
        const x = peste.querySelector('.alarm-close, .proc-close, .dlf-x, [data-mo="inchide"], [id$="-close"], [aria-label^="Închide"], .modal-close, .close-btn') as HTMLElement | null;
        if (x) x.click(); else peste.remove();
        return 1;
    }

    // panoul zilei din Calendar (30 sep 2026: „înapoi" arăta „Apasă din nou…" peste el)
    const ziCal = $id('cal-day-panel');
    if (ziCal && !ziCal.classList.contains('hidden') && $id('sec-calendar')?.classList.contains('active')) {
        ($id('cal-day-panel-close') as HTMLElement | null)?.click();
        return 1;
    }

    // Urgență → „Intervenții anterioare" / ghidul AI sunt trepte ale aceleiași pagini: „înapoi" duce
    // la formular, nu la „Apasă din nou pentru a părăsi aplicația" (30 sep 2026, turul ferestrelor).
    // Intervenția în lucru (cronometrul) NU se închide așa — rămâne cum e.
    if ($id('sec-journal')?.classList.contains('active')) {
        const pas = (id: string) => { const e = $id(id); return !!e && e.style.display !== 'none'; };
        if (pas('urg-phase-history')) { ($id('urg-history-back') as HTMLElement | null)?.click(); return 1; }
        if (pas('urg-phase-2')) { ($id('urg-back-btn') as HTMLElement | null)?.click(); return 1; }
    }

    return 0;
}

function _maintOnPopState(ev?: PopStateEvent): void {
    // O navigare pe `#fragment` (link `href="#…"`, `location.hash = …`) trimite și ea `popstate`,
    // dar ADAUGĂ o intrare în istoric; „înapoi" nu adaugă nimic. N-a fost un „înapoi", deci nu
    // închidem nimic și nu arătăm „Apasă din nou…" (Dan, 29 sep 2026). Starea intrării nu
    // ajută: `setRouteHash` o suprascrie cu `null` la fiecare schimbare de ecran.
    if (history.length > ((window as any)._maintHistLen || 0)) {
        (window as any)._maintHistLen = history.length;
        return;
    }
    if ((window as any)._maintHistorySkip > 0) {
        (window as any)._maintHistorySkip--;
        return;
    }
    const r = _maintCloseTopLayer();
    if (r === 2) return;
    if (r === 1) {
        try {
            history.pushState({ _maintPad: 1 }, '', location.href);
        } catch (e) {}
        (window as any)._maintHistLen = history.length;
        return;
    }

    const now = Date.now();
    const winMs = 2200;
    if (now - (window as any)._maintLastExitTap < winMs) {
        (window as any)._maintLastExitTap = 0;
        setTimeout(() => {
            try {
                history.back();
            } catch (e) {}
        }, 0);
        return;
    }
    (window as any)._maintLastExitTap = now;
    toast('Apasă din nou «Înapoi» pentru a părăsi aplicația', 'info');
    try {
        history.pushState({ _maintPad: 1 }, '', location.href);
    } catch (e) {}
    (window as any)._maintHistLen = history.length;
}

export function _initMaintHistoryBack(): void {
    if ((window as any)._maintHistBackInited) return;
    if (!window.history || !window.addEventListener) return;
    (window as any)._maintHistBackInited = true;
    try {
        history.replaceState({ _maintBase: 1 }, '', location.href);
        history.pushState({ _maintPad: 1 }, '', location.href);
    } catch (e) {}
    (window as any)._maintHistLen = history.length;
    window.addEventListener('popstate', _maintOnPopState);
    // Un link `href="#"` fără preventDefault în handlerul lui adaugă „#" în istoric și trimite
    // `popstate` — pentru paznic arată ca un „înapoi". Plasa din `_maintOnPopState` (history.length)
    // nu ține când istoricul a atins plafonul browserului (50 de intrări), deci oprim navigarea
    // la sursă. Handlerurile linkurilor rulează în continuare (preventDefault nu oprește clicul).
    document.addEventListener('click', (e) => {
        const a = (e.target as HTMLElement | null)?.closest?.('a[href="#"]');
        if (a) e.preventDefault();
    }, true);
}

export function escHtml(s: string | null | undefined): string {
    if (!s) return '—';
    const d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
}

export function truncate(s: string | null | undefined, n: number = 120): string {
    if (!s) return '—';
    return s.length > n ? s.slice(0, n) + '…' : s;
}

export function todayStr(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function localDateKey(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Formatează o dată în format românesc DD.MM.YYYY.
 * Acceptă: string ISO (YYYY-MM-DD), string ISO datetime, timestamp number sau Date.
 * Returnează '—' pentru valori invalide. */
export function formatDateRO(value: string | number | Date | null | undefined): string {
    if (value === null || value === undefined || value === '') return '—';
    let d: Date;
    if (value instanceof Date) {
        d = value;
    } else if (typeof value === 'number') {
        d = new Date(value);
    } else {
        const s = String(value).trim();
        const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) {
            d = new Date(parseInt(isoMatch[1], 10), parseInt(isoMatch[2], 10) - 1, parseInt(isoMatch[3], 10));
        } else {
            d = new Date(s);
        }
    }
    if (!d || isNaN(d.getTime())) return '—';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = d.getFullYear();
    return `${dd}.${mm}.${yy}`;
}

/** Formatează data + ora în format românesc DD.MM.YYYY HH:MM. */
export function formatDateTimeRO(value: string | number | Date | null | undefined, includeSeconds = false): string {
    if (value === null || value === undefined || value === '') return '—';
    let d: Date;
    if (value instanceof Date) {
        d = value;
    } else if (typeof value === 'number') {
        d = new Date(value);
    } else {
        let s = String(value).trim();
        if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}$/.test(s) && !s.endsWith('Z') && !s.includes('+')) {
            s = s.replace(' ', 'T') + 'Z';
        }
        d = new Date(s);
    }
    if (!d || isNaN(d.getTime())) return '—';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mn = String(d.getMinutes()).padStart(2, '0');
    if (includeSeconds) {
        const ss = String(d.getSeconds()).padStart(2, '0');
        return `${dd}.${mm}.${yy} ${hh}:${mn}:${ss}`;
    }
    return `${dd}.${mm}.${yy} ${hh}:${mn}`;
}

/* ═══ CLOCK ════════════════════════════════════════════════════ */
export function updateClock(): void {
    const now = new Date();
    const h = String(now.getHours()).padStart(2, '0');
    const m = String(now.getMinutes()).padStart(2, '0');
    const s = String(now.getSeconds()).padStart(2, '0');
    const dateStr = now.toLocaleDateString('ro-RO', { day: '2-digit', month: 'short', year: 'numeric' });
    $('#clock')!.textContent = `${dateStr} ${h}:${m}:${s}`;
}
setInterval(updateClock, 1000);
updateClock();

/* ═══ HOURS HISTORY (for sparklines) ══════════════════════════ */
export function recordHoursHistory(machineId: string, hours: number): void {
    if (!hours || hours <= 0) return;
    const hist = state.hoursHistory[machineId] || [];
    const last = hist.length > 0 ? hist[hist.length - 1] : null;
    if (!last || last.v !== hours) {
        hist.push({ t: Date.now(), v: hours });
        if (hist.length > 30) hist.splice(0, hist.length - 30);
        state.hoursHistory[machineId] = hist;
        localStorage.setItem('hoursHistory', JSON.stringify(state.hoursHistory));
    }
}

export function buildSparklineSVG(data: Array<{t: number, v: number}> | null, color: string): string {
    if (!data || data.length < 2) {
        return `<svg width="70" height="22" viewBox="0 0 70 22">
            <text x="35" y="14" text-anchor="middle" fill="#6b7590" font-size="7">fără date</text>
        </svg>`;
    }
    const vals = data.map(d => d.v);
    const w = 70, h = 22, pad = 2;
    const min = Math.min(...vals), max = Math.max(...vals);
    const range = max - min || 1;
    const pts = vals.map((v, i) => {
        const x = pad + (i / (vals.length - 1)) * (w - pad * 2);
        const y = pad + (1 - (v - min) / range) * (h - pad * 2);
        return `${x},${y}`;
    }).join(' ');
    const lastX = pad + ((vals.length - 1) / (vals.length - 1)) * (w - pad * 2);
    const lastY = pad + (1 - (vals[vals.length - 1] - min) / range) * (h - pad * 2);
    return `<svg width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
        <polyline points="${pts}" fill="none" stroke="${color}" stroke-width="1.2" stroke-linejoin="round" stroke-linecap="round"/>
        <circle cx="${lastX}" cy="${lastY}" r="1.5" fill="${color}"/>
    </svg>`;
}

/* ═══ ALERTS SYSTEM ════════════════════════════════════════════ */
/**
 * Calculează alertele "interval scadent / aproape scadent" pe baza intervalelor
 * de mentenanță și a ultimului log (real sau sintetic GO-LIVE) per (machine_id, action[:30]).
 *
 * 2026-05-16 fix: înainte folosea `hours % interval_hours`, ceea ce producea
 * alerte ciclice perpetue (ex: oh=3072, every=40h → rem=8 → mereu WARNING).
 * Acum:
 *   - Ignoră doar zilnicul (≤24h) — săptămânalul e 168h de funcționare, nu
 *     calendar; trebuie să apară în alertele pe ore.
 *   - Folosește `last_log_hours` din endpoint (subquery cu LIKE pe action[:30])
 *     pentru a calcula `hoursSinceLastLog = max(0, hours - last_log_hours)`.
 *   - Dacă nu există nicio înregistrare în maintenance_log pentru acel interval,
 *     baseline = 0 (ore totale) — comportament conservator.
 */
export function intervalHoursProgress(
    iv: { interval_hours?: number; last_log_hours?: number | null },
    machineHours: number,
): { hoursSince: number; remaining: number; ratio: number; overdue: boolean } {
    const ih = Number(iv.interval_hours) || 0;
    if (ih <= 0) return { hoursSince: 0, remaining: 0, ratio: 0, overdue: false };
    const lastLogRaw = iv.last_log_hours != null ? Number(iv.last_log_hours) : null;
    let lastSvcHours = lastLogRaw ?? 0;
    if (lastLogRaw === null && machineHours > ih) {
        lastSvcHours = Math.floor(machineHours / ih) * ih;
    }
    const hoursSince = Math.max(0, machineHours - lastSvcHours);
    const remaining = ih - hoursSince;
    const ratio = ih > 0 ? hoursSince / ih : 0;
    return { hoursSince, remaining, ratio, overdue: hoursSince >= ih };
}

/** Starea unei lucrări din plan — SURSA UNICĂ (1 nov 2026, auditul cerut de Dan): vine de pe server, din
 *  routes/maintenance/_stare.py (ultima lucrare, orele de pe server, calendar / ore, „depășit” = Restanțe), cu textul
 *  gata scris. Paginile n-o mai socotesc singure (`ore % interval`, lunarul ca 720 h, orele ținute în pagină). */
export interface StareLucrare {
    mod: 'ore' | 'calendar' | 'zilnic' | 'eveniment' | null;
    nivel: 'depasit' | 'aproape' | 'ok' | 'fara_ore' | 'info';
    text: string;
    depasit: boolean;
    ultima_data: string | null;
    ultima_ore: number | null;
    ore_scurse: number | null;
    ore_ramase: number | null;
    procent: number | null;
    scadenta: string | null;
    zile: number | null;
    ultima_estimata?: boolean;
}

/** Eticheta colorată a stării — aceeași în toate paginile. */
export function badgeStare(st: StareLucrare | null | undefined): string {
    if (!st || !st.text) return '<span class="badge badge-gray">—</span>';
    const cls = st.nivel === 'depasit' ? 'badge-red' : st.nivel === 'aproape' ? 'badge-yellow'
        : (st.nivel === 'fara_ore' || st.nivel === 'info') ? 'badge-gray' : 'badge-green';
    const tip = [st.ultima_data ? `ultima lucrare: ${String(st.ultima_data).slice(0, 10)}` : 'nicio lucrare înregistrată',
        st.ultima_ore != null && st.mod === 'ore' ? `la ${Math.round(st.ultima_ore)} h${st.ultima_estimata ? ' (estimat)' : ''}` : '',
        st.mod === 'calendar' ? 'pe calendar (cu opririle liniei)' : st.mod === 'ore' ? 'pe orele mașinii' : '']
        .filter(Boolean).join(' · ');
    return `<span class="badge ${cls}" title="${escHtml(tip)}">${escHtml(st.text)}</span>`;
}

/** Câte ore „mai sunt” după stare (pe calendar: zilele × 24) — pentru sortare / ferestre; null = fără scadență. */
export function oreRamaseStare(st: StareLucrare | null | undefined): number | null {
    if (!st || (st.mod !== 'ore' && st.mod !== 'calendar') || st.nivel === 'fara_ore') return null;
    if (st.depasit) return 0;
    if (st.mod === 'ore') return Math.max(0, st.ore_ramase ?? 0);
    return st.zile == null ? null : Math.max(0, st.zile * 24);
}

/** Linie nepornită / Pre-GO-LIVE — ascunde TOATE alertele de mentenanță (hide_all). */
export function shouldSuppressLineMaintenanceAlerts(line: {
    is_active?: number | boolean;
    started_at?: string | null;
} | null | undefined): boolean {
    if (!line) return false;
    const active = line.is_active === 1 || line.is_active === true;
    const started = !!(line.started_at && String(line.started_at).trim());
    return !active || !started;
}

/** Four-state filter: hide inactive / technical_pause intervals from due views. */
export function shouldHideMaintenanceInterval(
    iv: { machine_state?: string; should_hide_alert?: boolean },
): boolean {
    const st = (iv.machine_state as string) || 'active';
    // 'no_data' (audit 2.13): utilaj care avea telemetrie și a amuțit. Scadențele
    // ies dintr-un contor mort, deci se ascund la fel ca la pauza tehnică.
    if (st === 'technical_pause' || st === 'inactive' || st === 'no_data') return true;
    if (iv.should_hide_alert === true) return true;
    return false;
}

/** Include interval in due/next views; maintenance state → critical only. */
export function shouldShowMaintenanceInterval(
    iv: { machine_state?: string; critical?: number | boolean },
): boolean {
    if (shouldHideMaintenanceInterval(iv)) return false;
    const st = (iv.machine_state as string) || 'active';
    if (st === 'maintenance' && !iv.critical) return false;
    return true;
}

/** Filtrează alerte mentenanță pentru o linie (respectă hide_all la nivel de linie). */
export function filterMaintenanceAlertsForLine<T extends { machine_id?: string }>(
    alerts: T[],
    pl: { id?: string; equipment?: Array<{ id: string }> },
    lineMeta?: { is_active?: number | boolean; started_at?: string | null },
): T[] {
    if (shouldSuppressLineMaintenanceAlerts(lineMeta)) return [];
    return alerts.filter(a =>
        (pl.equipment || []).some(eq => eq.id === a.machine_id));
}

export function computeAlerts(intervals: any[]): Alert[] {
    const alerts: Alert[] = [];
    intervals.forEach(iv => {
        const ih = Number(iv.interval_hours) || 0;
        if (ih <= 0) return;
        // Skip doar zilnic (8h/24h) — sunt în planul fiecărei zile.
        if (ih <= 24) return;

        // 2026-05-24 — Four-state model: filtrare după starea mașinii.
        //   - 'technical_pause' / 'inactive' → ascunse complet (clock înghețat).
        //   - 'maintenance'                  → doar critice (sugestii „cât ești oprit").
        //   - 'active'                       → comportament normal.
        // Backend-ul atașează `machine_state` și `should_hide_alert` pe fiecare interval
        // în `/api/maintenance_intervals`.
        const machineState: string = (iv.machine_state as string) || 'active';
        if (machineState === 'technical_pause' || machineState === 'inactive'
            || machineState === 'no_data') return;
        if (iv.should_hide_alert === true) return;

        // 1 nov 2026 (auditul surselor de ore): starea vine de pe server (`iv.stare`, routes/maintenance/_stare.py).
        // „critic” = DEPĂȘIT, adică exact lucrările din Restanțe; „atenție” = aproape (≥ 90 % pe ore sau ≤ 3 zile pe
        // calendar). Înainte: pe ore din orele ținute în pagină, lunarul ca 720 h, „critic” de la 90 %.
        const st = iv.stare as StareLucrare | undefined;
        if (!st || (st.mod !== 'ore' && st.mod !== 'calendar')) return;
        const remaining = oreRamaseStare(st) ?? 0;
        const alarmKey = `${iv.machine_id}:${iv.id}`;

        const base: Partial<Alert> = {
            id: alarmKey,
            machine_id: iv.machine_id,
            action: iv.action_ro,
            interval: ih,
            category: iv.category,
            last_log_date: iv.last_log_date || null,
            last_log_synthetic: iv.last_log_synthetic === 1 || iv.last_log_synthetic === true,
            text: st.text,
        };

        if (st.depasit) {
            // 'maintenance' state: surface critical as "while you're stopped" suggestion.
            const isMaintSuggestion = machineState === 'maintenance';
            alerts.push({
                ...base,
                severity: 'critical',
                remaining: 0,
                maintenance_suggestion: isMaintSuggestion,
                machine_state: machineState,
            } as Alert);
        } else if (st.nivel === 'aproape') {
            // For 'maintenance' state we deliberately drop warnings — the
            // tech is already busy with the open workflow; only critical
            // alerts deserve the "while you're stopped" interruption.
            if (machineState === 'maintenance') return;
            alerts.push({
                ...base,
                severity: 'warning',
                remaining,
                machine_state: machineState,
            } as Alert);
        }
    });
    alerts.sort((a, b) => {
        if (a.severity === 'critical' && b.severity !== 'critical') return -1;
        if (b.severity === 'critical' && a.severity !== 'critical') return 1;
        return (a.remaining as number) - (b.remaining as number);
    });
    return alerts;
}

/* ═══ PANOUL DE ALARME, PE CATEGORII (19 sep 2026) ═══════════════════════
 * Panoul arăta DOAR ce vine de la utilaje. Tot ce scriu veghetorii noștri —
 * inclusiv «SFM nu mai trimite date de 102 zile» sau «colectorul liniei ROBOT
 * e mort» — trăia pe altă pagină, deschisă doar de master. Acum sunt în același
 * loc, pe două file: «Utilaje» (de la mașini + ce scrie aplicația DESPRE mașini)
 * și «Aplicație» (AI, documente, teste, costuri), asta din urmă doar la master.
 */
interface IncidentAplicatie {
    id: number; tip: string; titlu: string; corp: string;
    severitate: string; created_at: string; eticheta: string; categorie: string;
    /** ultima apariție (verificarea a văzut-o din nou cât era necitită); gol = o singură apariție */
    ultima_la?: string | null;
    /** 10 oct 2026: utilajul și linia (liniile, la utilitățile comune) alertei, când e despre un utilaj. */
    machine_id?: string | null; linii?: string[];
}
const incidente: { utilaje: IncidentAplicatie[]; aplicatie: IncidentAplicatie[]; vedeAplicatie: boolean } =
    { utilaje: [], aplicatie: [], vedeAplicatie: false };

let filaAlarme: 'utilaje' | 'aplicatie' = 'utilaje';
/** Panoul deschis de pe cardul unei linii arată doar atenționările ei (10 oct 2026). */
let liniaAlarme: string | null = null;

/** Ia alarmele scrise de aplicație. Tace la eroare: panoul trebuie să se
 *  deschidă și dacă ruta asta cade — alarmele de utilaj sunt mai importante. */
export async function incarcaIncidenteAplicatie(): Promise<void> {
    try {
        const r = await fetch('/api/alarme/incidente' + (liniaAlarme ? `?linie=${encodeURIComponent(liniaAlarme)}` : ''));
        if (!r.ok) { incidente.vedeAplicatie = false; return; }
        const d = await r.json();
        incidente.utilaje = d.utilaje || [];
        incidente.aplicatie = d.aplicatie || [];
        incidente.vedeAplicatie = !!d.vede_aplicatie;
    } catch {
        incidente.utilaje = []; incidente.aplicatie = []; incidente.vedeAplicatie = false;
    }
}

/* ═══ abaterile de la normalul învățat (R3, 28 sep 2026) ═════════════════════
 * Detectorul (scripts/normal_si_abateri.py) compară în fiecare oră fiecare cheie
 * a fiecărui utilaj cu normalul ei din orele de producție. În primele 7 zile
 * (rodajul — decizia lui Dan din 27 sep) abaterile NU sună: se văd aici, într-o
 * secțiune informativă, și NU intră în cifra butonului de alarmă.
 */
interface AbatereNormal {
    machine_id: string; sensor_key: string; tip: string; directie: string; ore: number;
    ultima_ora: string; max_consecutive: number; z_max: number;
    valoare?: number; medie?: number; sigma?: number; z?: number;
}
const abateri: { lista: AbatereNormal[]; inRodaj: boolean; rodajPana: string | null; incarcat: boolean; deschis: boolean } =
    { lista: [], inRodaj: false, rodajPana: null, incarcat: false, deschis: false };

/** Tace la eroare (operatorii primesc 403 — secțiunea e pentru ingineri). */
export async function incarcaAbateri(): Promise<void> {
    try {
        const r = await fetch('/api/abateri_normal?ore=48');
        if (!r.ok) { abateri.incarcat = false; return; }
        const d = await r.json();
        abateri.lista = Array.isArray(d.abateri) ? d.abateri : [];
        abateri.inRodaj = !!d.in_rodaj;
        abateri.rodajPana = d.rodaj_pana_la || null;
        abateri.incarcat = !!d.activ;
    } catch {
        abateri.incarcat = false;
    }
}

const ETICHETE_ABATERE: Record<string, string> = {
    nivel: 'nivel', instabilitate: 'instabil în oră', rata: 'ritm pe oră',
};

function fmtNr(v: unknown): string {
    const n = Number(v);
    if (!isFinite(n)) return '—';
    const a = Math.abs(n);
    return a >= 1000 ? n.toFixed(0) : a >= 10 ? n.toFixed(1) : n.toFixed(2);
}

function sectiuneAbateri(): HTMLElement | null {
    if (!abateri.incarcat) return null;
    const el = document.createElement('details');
    el.className = 'abateri-normal';
    el.open = abateri.deschis;
    el.addEventListener('toggle', () => { abateri.deschis = el.open; });
    const pana = abateri.rodajPana ? abateri.rodajPana.slice(0, 10).split('-').reverse().join('.') : '';
    const rodaj = abateri.inRodaj
        ? `<span class="abateri-rodaj">rodaj până la ${escHtml(pana)} — se văd, nu sună</span>` : '';
    let corp = '';
    if (!abateri.lista.length) {
        corp = '<div class="abateri-gol">Nicio abatere de la normal în ultimele 48 de ore.</div>';
    } else {
        corp = abateri.lista.map(a => {
            const sus = a.directie === 'peste';
            const ora = String(a.ultima_ora || '').slice(5, 16).replace(' ', ' ora ');
            return `<div class="abatere-rand">
                <div class="abatere-cap"><strong>${escHtml(a.machine_id)}</strong> · ${escHtml(a.sensor_key)}
                    <span class="abatere-tip">${escHtml(ETICHETE_ABATERE[a.tip] || a.tip)} ${sus ? '▲' : '▼'}</span></div>
                <div class="abatere-detalii">${fmtNr(a.valoare)} față de normalul ${fmtNr(a.medie)}
                    · ${a.ore} ${a.ore === 1 ? 'oră' : 'ore'} în 48 h, cel mult ${a.max_consecutive} la rând · ultima: ${escHtml(ora)} UTC</div>
            </div>`;
        }).join('');
    }
    el.innerHTML = `<summary>ABATERI DE LA NORMAL (${abateri.lista.length}) ${rodaj}</summary>${corp}`;
    return el;
}

/** Alarmele de utilaj neconfirmate — aceeași regulă ca panoul dintotdeauna. */
function alarmeUtilajNeconfirmate(): any[] {
    const combined = [
        ...(state.controllerAlarms || []), ...(state.allAlerts || []),
        ...(state.sensorAlarms || []), ...(state.openCaseAlerts || []),
    ];
    return combined.filter(a => a.source === 'controller' ? !a.acked
                                                         : !state.ackedAlarms.includes(a.id));
}

function numaraAlarme(): { utilaje: number; aplicatie: number; critice: number } {
    const ut = alarmeUtilajNeconfirmate();
    return {
        utilaje: ut.length + incidente.utilaje.length,
        aplicatie: incidente.aplicatie.length,
        critice: ut.filter(a => a.severity === 'critical').length
               + incidente.utilaje.filter(i => i.severitate === 'critical').length,
    };
}

/** Butonul mare din antet: cifra, culoarea, clipitul. */
export function actualizeazaButonAlarme(): void {
    const btn = $('#alarme-buton') as HTMLButtonElement | null;
    if (!btn) return;
    const n = numaraAlarme();
    const total = n.utilaje + n.aplicatie;
    btn.style.display = '';
    btn.classList.toggle('gol', total === 0);
    btn.classList.toggle('critic', n.critice > 0);
    const nr = $('#alarme-buton-nr');
    if (nr) nr.textContent = String(total);
    btn.title = total === 0
        ? 'Nicio alarmă activă'
        : `${n.utilaje} pe utilaje${n.aplicatie ? `, ${n.aplicatie} pe aplicație` : ''}`
          + (n.critice ? ` · ${n.critice} critice` : '');

    // filele își poartă cifra lor
    const fUt = $('[data-nr="utilaje"]'); if (fUt) fUt.textContent = String(n.utilaje);
    const fAp = $('[data-nr="aplicatie"]'); if (fAp) fAp.textContent = String(n.aplicatie);
    const filaAp = $('.alarm-fila[data-fila="aplicatie"]') as HTMLElement | null;
    if (filaAp) filaAp.hidden = !incidente.vedeAplicatie;
}

export function updateAlarmBadge(_count?: number): void {
    // P2: separăm vizual cele 3 surse de alerte în topbar.
    // Parametrul `_count` e ignorat; recalculăm direct din state ca să fim mereu sincronizați
    // cu ce arată panoul de alarme (acelaşi filtru de unacked).
    // 20 sep 2026 (Dan: «acum avem două butoane de alarmă»): pastilele astea
    // stăteau în antet, lângă butonul mare, și deschideau ACELAȘI panou — două
    // uși pentru același lucru. Acum sunt doar informație, în capul panoului:
    // arată din ce sursă vin alarmele, fără să mai fie a doua cale de intrare.
    const badge = $('#alarm-surse') as HTMLElement;
    if (!badge) return;

    const ctrlCount = (state.controllerAlarms || []).filter(a => !a.acked).length;
    const maintCount = [
        ...(state.allAlerts || []),
        ...(state.sensorAlarms || []),
    ].filter(a => !state.ackedAlarms.includes(a.id)).length;
    const aiCount = (state.openCaseAlerts || []).filter(a => !state.ackedAlarms.includes(a.id)).length;
    const total = ctrlCount + maintCount + aiCount;

    if (total === 0) {
        badge.style.display = 'none';
        badge.innerHTML = '';
        actualizeazaButonAlarme();   // butonul rămâne, dar stins pe verde
        return;
    }

    // 1-3 pilule colorate pentru fiecare sursă cu count > 0, în capul panoului.
    // Galben = controller HMI/PLC (alarme native maşină)
    // Roşu  = mentenanţă (intervale depăşite + praguri senzori)
    // Albastru = AI / cazuri diagnostic deschise
    const pills: string[] = [];
    if (ctrlCount > 0) {
        pills.push(`<span class="al-pill al-ctrl" title="Alarme controller HMI/PLC">⚙ ${ctrlCount}</span>`);
    }
    if (maintCount > 0) {
        pills.push(`<span class="al-pill al-maint" title="Alerte mentenanță (intervale + senzori)">🔧 ${maintCount}</span>`);
    }
    if (aiCount > 0) {
        pills.push(`<span class="al-pill al-ai" title="Cazuri diagnostic AI deschise">🤖 ${aiCount}</span>`);
    }
    badge.style.display = '';
    badge.innerHTML = pills.join('');
    actualizeazaButonAlarme();
}

/** Cardul unei alarme scrise de aplicație (nu de utilaj). */
/** Prima și ultima apariție, în ora României (11 oct 2026): alerta testelor de noapte se repeta în fiecare noapte,
 *  dar cardul arăta doar „2026-09-19 00:13” (UTC, prima dată) — o problemă de azi părea de acum trei săptămâni. */
function _cand_incident(i: IncidentAplicatie): string {
    const prima = formatDateTimeRO(i.created_at);
    if (!i.ultima_la) return prima;
    const t0 = Date.parse(i.created_at.replace(' ', 'T') + 'Z'), t1 = Date.parse(i.ultima_la.replace(' ', 'T') + 'Z');
    if (!(t1 - t0 > 60000)) return prima;
    return `ultima ${formatDateTimeRO(i.ultima_la)} · prima ${prima}`;
}

function cardIncident(i: IncidentAplicatie): HTMLElement {
    const div = document.createElement('div');
    div.className = `alarm-item ${i.severitate === 'critical' ? 'critical' : (i.severitate === 'info' ? 'info' : 'warning')} alarm-item-clickable`;
    div.dataset.incidentId = String(i.id);
    div.title = 'Clic pentru textul întreg';
    const linii = (i.linii || []).length ? `<span class="alarm-linie" title="Linia">${escHtml((i.linii || []).join(', '))}</span>` : '';
    div.innerHTML = `
        <div class="alarm-item-header">
            <span class="alarm-machine">${escHtml(i.eticheta)}${linii}</span>
            <span class="alarm-severity ${escHtml(i.severitate || 'info')}">${escHtml((i.severitate || 'info').toUpperCase())}</span>
        </div>
        <div class="alarm-action">${escHtml(i.titlu || '')}</div>
        <div class="alarm-meta">
            <span>${escHtml(_cand_incident(i))}</span>
            <button type="button" class="alarm-ack alarm-vezi" data-incident-vezi="${i.id}">Citește</button>
        </div>`;
    return div;
}

/** Clic pe un card de incident → textul întreg. În fâșia îngustă corpul era
 *  tăiat și nu se putea deschide (Dan, 21 aug: «apas pe ea și nu se întâmplă
 *  nimic, nu pot citi tot ce scrie»). */
function legaClicIncidente(gazda: Element): void {
    (gazda.querySelectorAll('[data-incident-id]') as NodeListOf<HTMLElement>).forEach(card => {
        if (card.dataset.legat === '1') return;
        card.dataset.legat = '1';
        card.addEventListener('click', () => {
            const id = Number(card.dataset.incidentId);
            const inc = [...incidente.utilaje, ...incidente.aplicatie].find(x => x.id === id);
            if (!inc) return;
            const corp = card.querySelector('.alarm-incident-corp') as HTMLElement | null;
            const buton = card.querySelector('[data-incident-vezi]') as HTMLElement | null;
            if (corp) { corp.remove(); if (buton) buton.textContent = 'Citește'; return; }   // a doua apăsare închide
            if (buton) buton.textContent = 'Ascunde';
            const el = document.createElement('div');
            el.className = 'alarm-cause alarm-incident-corp';
            el.style.whiteSpace = 'pre-wrap';
            el.style.marginTop = '.5rem';
            el.textContent = inc.corp || '(fără detalii)';
            card.insertBefore(el, card.querySelector('.alarm-meta'));
        });
    });
}

export function renderAlarmPanel(): void {
    const list = $('#alarm-list')!;

    // file: care e activă și ce se vede pe ea
    (document.querySelectorAll('.alarm-fila') as NodeListOf<HTMLElement>).forEach(f => {
        const a = f.dataset.fila === filaAlarme;
        f.classList.toggle('activ', a);
        f.setAttribute('aria-selected', String(a));
    });

    if (liniaAlarme && filaAlarme === 'utilaje') {
        // deschis de pe cardul liniei: doar atenționările ei, cu drum înapoi la lista completă
        list.innerHTML = '';
        const cap = document.createElement('div');
        cap.className = 'alarm-grup-titlu alarm-linie-cap';
        cap.innerHTML = `<span>ATENȚIONĂRI — LINIA ${escHtml(liniaAlarme)} (${incidente.utilaje.length})</span>
            <button type="button" class="alarm-ack" id="alarm-toate-liniile">Toate liniile</button>`;
        list.appendChild(cap);
        if (!incidente.utilaje.length) {
            const gol = document.createElement('div');
            gol.className = 'alarm-empty';
            gol.innerHTML = `<span class="icon">✅</span>Nicio atenționare necitită pe linia ${escHtml(liniaAlarme)}`;
            list.appendChild(gol);
        }
        incidente.utilaje.forEach(i => list.appendChild(cardIncident(i)));
        legaClicIncidente(list);
        (list.querySelector('#alarm-toate-liniile') as HTMLElement | null)?.addEventListener('click', () => {
            liniaAlarme = null;
            void incarcaIncidenteAplicatie().then(() => renderAlarmPanel());
        });
        return;
    }

    if (filaAlarme === 'aplicatie') {
        list.innerHTML = '';
        if (!incidente.aplicatie.length) {
            list.innerHTML = `<div class="alarm-empty"><span class="icon">✅</span>Nicio alarmă a aplicației</div>`;
            return;
        }
        incidente.aplicatie.forEach(i => list.appendChild(cardIncident(i)));
        legaClicIncidente(list);
        return;
    }

    const unacked = alarmeUtilajNeconfirmate();
    const combined = [...(state.controllerAlarms || []), ...(state.allAlerts || []), ...(state.sensorAlarms || []), ...(state.openCaseAlerts || [])];

    if (unacked.length === 0 && incidente.utilaje.length === 0) {
        list.innerHTML = `<div class="alarm-empty"><span class="icon">✅</span>Nicio alarmă activă</div>`;
        const sAb = sectiuneAbateri();
        if (sAb) list.appendChild(sAb);
        return;
    }

    list.innerHTML = '';
    unacked.forEach(a => {
        const div = document.createElement('div');
        const isOpenCase = a.severity === 'open_case';
        const isController = a.source === 'controller';
        div.className = `alarm-item ${a.severity}`;
        const isSensor = a.category === 'SENZOR';

        if (isController) {
            const timeAgo = a.created_at ? _timeAgo(a.created_at) : '';
            div.classList.add('alarm-item-clickable');
            div.dataset.controllerAlarm = '1';
            div.dataset.machineId = a.machine_id;
            div.dataset.alarmCode = String(a.alarm_code || '');
            div.title = 'Click pentru detalii din manual';
            div.innerHTML = `
                <div class="alarm-item-header">
                    <span class="alarm-machine">${escHtml(a.machine_name || a.machine_id)}</span>
                    <span class="alarm-severity ${a.severity}">${a.severity.toUpperCase()}</span>
                </div>
                <div class="alarm-action">${escHtml(a.action)}</div>
                <div class="alarm-meta">
                    <span>COD ${escHtml(a.alarm_code)} · ${a.alarm_type || 'fault'} · ${timeAgo}</span>
                    <button class="alarm-ack" data-alarm-id="${a.id}" data-db-id="${a.db_id}" data-controller="1">ACK</button>
                </div>
            `;
        } else {
            // 7 sep 2026 (Dan: «restul nu se deschid să le citesc, rămân doar
            // acolo»): până azi doar alarmele de controler aveau click. În
            // coloana îngustă textul e tăiat, deci rândurile astea nu se puteau
            // nici citi, nici deschide.
            div.classList.add('alarm-item-clickable');
            div.dataset.alertId = String(a.id);
            div.dataset.alertKind = isOpenCase ? 'open_case' : (isSensor ? 'sensor' : 'maintenance');
            div.title = 'Click pentru detalii';
            const remText = isSensor ? '' : (a.text ? a.text : (a.remaining != null && a.remaining <= 0 ? 'DEPĂȘIT' : (a.remaining != null ? `${Math.round(a.remaining)}h rămase din ${a.interval}h` : '')));
            const metaText = isOpenCase ? (a.outcome === 'partial' ? 'Parțial rezolvat' : 'Nerezolvat') : (isSensor ? a.action : `${escHtml(a.category || '')} · ${remText}`);
            div.innerHTML = `
                <div class="alarm-item-header">
                    <span class="alarm-machine">${escHtml(a.machine_id)}</span>
                    <span class="alarm-severity ${a.severity}">${isOpenCase ? 'CAZ DESCHIS' : a.severity.toUpperCase()}</span>
                </div>
                <div class="alarm-action">${escHtml(a.action || 'Mentenanță necesară')}</div>
                <div class="alarm-meta">
                    <span>${metaText}</span>
                    ${isOpenCase ? `<button type="button" class="alarm-ack alarm-vezi" data-alarm-id="${a.id}" data-section="diagnostic">Vezi</button>` : `<button class="alarm-ack" data-alarm-id="${a.id}">ACK</button>`}
                </div>
            `;
        }
        list.appendChild(div);
    });

    // Ce scrie APLICAȚIA despre utilaje — mașini mute, colectoare moarte, ceasuri
    // nesincrone. Până azi stăteau pe altă pagină, doar la master; inginerul
    // n-avea de unde să știe că M5 tace de 102 zile.
    if (incidente.utilaje.length) {
        const titlu = document.createElement('div');
        titlu.className = 'alarm-grup-titlu';
        titlu.textContent = `SEMNALATE DE APLICAȚIE (${incidente.utilaje.length})`;
        list.appendChild(titlu);
        incidente.utilaje.forEach(i => list.appendChild(cardIncident(i)));
        legaClicIncidente(list);
    }
    const sAbateri = sectiuneAbateri();
    if (sAbateri) list.appendChild(sAbateri);

    (list.querySelectorAll('.alarm-item-clickable') as NodeListOf<HTMLElement>).forEach(item => {
        item.addEventListener('click', (ev) => {
            if ((ev.target as HTMLElement).closest('.alarm-ack')) return;
            if (item.dataset.incidentId) return;   // are propriul ascultător

            if (item.dataset.alertKind) {
                const alerta = combined.find(x => String(x.id) === item.dataset.alertId);
                if (!alerta) return;
                const kind = item.dataset.alertKind as 'maintenance' | 'sensor' | 'open_case';
                openAlertDetailModal({
                    machine_id: alerta.machine_id,
                    machine_name: alerta.machine_name || alerta.machine_id,
                    severity: alerta.severity,
                    action: alerta.action,
                    category: alerta.category,
                    interval: alerta.interval,
                    remaining: alerta.remaining,
                    text: alerta.text,
                    last_log_date: alerta.last_log_date,
                    last_log_synthetic: alerta.last_log_synthetic,
                    outcome: alerta.outcome,
                    kind,
                    onMachine: () => {
                        if ((window as any).openLineDetail) {
                            (window as any).openLineDetail(alerta.machine_id, alerta.machine_name || alerta.machine_id);
                        }
                    },
                    onDiagnostic: kind === 'open_case'
                        ? () => { (window as any).showSection?.('diagnostic'); }
                        : undefined,
                    onAck: () => {
                        if (!state.ackedAlarms.includes(alerta.id)) state.ackedAlarms.push(alerta.id);
                        localStorage.setItem('ackedAlarms', JSON.stringify(state.ackedAlarms));
                        renderAlarmPanel();
                        updateAlarmBadge(0);
                    },
                });
                return;
            }

            const machineId = item.dataset.machineId || '';
            const alarmCode = item.dataset.alarmCode || '';
            if (!machineId || !alarmCode) return;
            const alarm = (state.controllerAlarms || []).find(
                x => x.machine_id === machineId && String(x.alarm_code) === alarmCode,
            );
            openControllerAlarmDetailModal({
                machine_id: machineId,
                machine_name: alarm?.machine_name || machineId,
                alarm_code: alarmCode,
                alarm_type: alarm?.alarm_type,
                severity: alarm?.severity,
                action: alarm?.action,
                cause_ro: alarm?.cause_ro,
                solution_ro: alarm?.solution_ro,
                par1: alarm?.par1,
                par2: alarm?.par2,
                created_at: alarm?.created_at,
                db_id: alarm?.db_id,
                title: '🔔 Alarmă controler',
                onHistory: () => { void openAlarmHistoryForMachine(machineId); },
                onSensors: () => {
                    (window as any).detailTabActive = 'sensors';
                    if ((window as any).openLineDetail) (window as any).openLineDetail(machineId, alarm?.machine_name || machineId);
                },
                onMachine: () => {
                    (window as any).detailTabActive = 'parts';
                    if ((window as any).openLineDetail) (window as any).openLineDetail(machineId, alarm?.machine_name || machineId);
                },
            });
        });
    });

    // „Citește" de pe alarmele scrise de aplicație are aceeași clasă, dar NU e un ACK:
    // prins aici, își oprea atingerea înainte de card, deci nu deschidea nimic, și mai
    // adăuga o confirmare goală în `ackedAlarms` (Dan, 29 sep 2026: „Citește nu face nimic").
    (list.querySelectorAll('.alarm-ack:not([data-incident-vezi])') as NodeListOf<HTMLButtonElement>).forEach(btn => {
        btn.addEventListener('click', async (ev) => {
            ev.stopPropagation();
            const isOpenCase = (btn.dataset.alarmId || '').startsWith('diagcase:');
            const isCtrl = btn.dataset.controller === '1';
            if (isCtrl) {
                const dbId = btn.dataset.dbId;
                if (dbId) {
                    try {
                        await fetch(`/api/alarms/${dbId}/ack`, {
                            method: 'POST',
                            headers: {'Content-Type': 'application/json'},
                            body: JSON.stringify({acked_by: state.currentUser?.username || 'operator'}),
                        });
                        const alarm = state.controllerAlarms!.find(a => (a.db_id as unknown as string) == dbId);
                        if (alarm) alarm.acked = true;
                    } catch (e) {}
                }
            } else if (!isOpenCase) {
                state.ackedAlarms.push(btn.dataset.alarmId as string);
                localStorage.setItem('ackedAlarms', JSON.stringify(state.ackedAlarms));
            }
            if (btn.dataset.section === 'diagnostic') {
                (document.querySelector('.nav-links a[data-section="diagnostic"]') as HTMLElement | null)?.click();
            }
            const all = [...(state.controllerAlarms || []), ...(state.allAlerts || []), ...(state.sensorAlarms || []), ...(state.openCaseAlerts || [])];
            updateAlarmBadge(all.filter(x => {
                if (x.source === 'controller') return !x.acked;
                return !state.ackedAlarms.includes(x.id);
            }).length);
            renderAlarmPanel();
        });
    });
}

export function _timeAgo(dateStr: string): string {
    try {
        const d = new Date(dateStr + 'Z');
        const diff = (Date.now() - d.getTime()) / 1000;
        if (diff < 60) return 'acum';
        if (diff < 3600) return `${Math.floor(diff / 60)} min`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
        return `${Math.floor(diff / 86400)}z`;
    } catch (e) { return ''; }
}

/* ═══ ALARM HISTORY (tab-uri linie → componentă) ═══ */
let _ahPage = 1;
const _ahPerPage = 50;
let _ahSelectedLine = '';
let _ahSelectedMachine = '';
let _ahLinesCache: Array<{ id: string; product?: string; equipment: Array<{ id: string; name?: string; display_name?: string }> }> = [];
let _ahSummaryCache: Record<string, { total_active?: number; unacked_count?: number }> = {};
/* A ales omul o linie, sau e alegerea noastră implicită? Implicit deschideam
 * mereu M1 — care duminică era gol, în timp ce M8 avea 9 alarme și robotul 8
 * (audit 20 sep 2026: «primul ecran zice „nimic"; alarmele reale sunt 2–3
 * click-uri mai departe»). Câtă vreme omul n-a apăsat pe o filă, deschidem
 * prima linie care ARE alarme neconfirmate. */
let _ahUserChose = false;

function _neconfirmatePeLinie(pl: { equipment: any[] }): number {
    return (pl.equipment || []).reduce((n: number, eq: any) => {
        const s = _ahSummaryCache[eq.id];
        return n + (s?.unacked_count ?? s?.total_active ?? 0);
    }, 0);
}
function _primaLinieCuAlarme(): string {
    const cu = _ahLinesCache.find(l => _neconfirmatePeLinie(l) > 0);
    return cu ? cu.id : (_ahLinesCache[0]?.id || '');
}

export function openAlarmHistoryForLine(lineId: string, machineId?: string): void {
    _ahSelectedLine = lineId || '';
    _ahUserChose = !!lineId;   // cerută explicit din alt ecran = alegerea omului
    _ahSelectedMachine = machineId || '';
    if ((window as any).showSection) (window as any).showSection('sec-alarm_history');
    else document.querySelector('.nav-item[data-section="alarm_history"]')?.dispatchEvent(new MouseEvent('click', { bubbles: true }));
    void loadAlarmHistory(1);
}

export async function openAlarmHistoryForMachine(machineId: string): Promise<void> {
    await _ensureAhLines();
    let lineId = '';
    for (const pl of _ahLinesCache) {
        if ((pl.equipment || []).some(e => e.id === machineId)) {
            lineId = pl.id;
            break;
        }
    }
    if (!lineId) {
        try {
            await _ensureMachineCache();
            const m = (_allMachinesCache || []).find(x => x.id === machineId);
            lineId = ((m?.line || '') as string).split(',')[0].trim() || '';
        } catch { /* ignore */ }
    }
    _ahSelectedLine = lineId || _ahLinesCache[0]?.id || '';
    _ahUserChose = !!lineId;   // idem: linia cerută din tablou nu se suprascrie
    _ahSelectedMachine = machineId;
    if ((window as any).showSection) (window as any).showSection('sec-alarm_history');
    else document.querySelector('.nav-item[data-section="alarm_history"]')?.dispatchEvent(new MouseEvent('click', { bubbles: true }));
    void loadAlarmHistory(1);
}

async function _ensureAhLines(): Promise<void> {
    if (_ahLinesCache.length) return;
    try {
        const struct = await api('/api/factory_structure');
        const lines = (struct.production_lines || []).map((pl: any) => ({
            id: pl.id,
            product: pl.product || '',
            equipment: pl.equipment || [],
        }));
        const utils = struct.utilities || {};
        Object.entries(utils).forEach(([uKey, u]: [string, any]) => {
            lines.push({
                id: uKey,
                product: u.label || uKey,
                equipment: u.machines || [],
            });
        });
        _ahLinesCache = lines.filter((l: any) => l.equipment.length > 0);
        if (!_ahSelectedLine && _ahLinesCache.length) {
            _ahSelectedLine = _ahLinesCache[0].id;
        }
    } catch (e) {
        console.warn('[alarm-history] factory_structure failed', e);
    }
}

function _renderAhLineTabs(): void {
    const el = document.getElementById('ah-line-tabs');
    if (!el) return;
    if (!_ahLinesCache.length) {
        el.innerHTML = '<span class="ah-empty-hint">Se încarcă liniile...</span>';
        return;
    }
    // 8 aug 2026 — `||` în loc de `??`: când `unacked_count` ajunge la ZERO (o
    // valoare falsă), cădea pe `total_active`, deci insigna NU ajungea niciodată
    // la zero după confirmarea alarmelor. Găsit prin măsurare, nu prin citire:
    // testul confirma în bloc toate alarmele unei linii, dovedea în bază că nu
    // mai există neconfirmate, și insigna arăta în continuare un număr.
    el.innerHTML = _ahLinesCache.map(pl => {
        const sum = (pl.equipment || []).reduce((n, eq) => {
            const s = _ahSummaryCache[eq.id];
            return n + (s?.unacked_count ?? s?.total_active ?? 0);
        }, 0);
        const badge = sum > 0 ? `<span class="ah-tab-badge">${sum}</span>` : '';
        const active = pl.id === _ahSelectedLine ? ' ah-tab-active' : '';
        return `<button type="button" class="ah-line-tab${active}" data-line="${escHtml(pl.id)}" role="tab" aria-selected="${pl.id === _ahSelectedLine}">
            ${escHtml(pl.id)}${badge}
        </button>`;
    }).join('');
    // ACK în bloc pe linia selectată (audit UX 31 iul 2026 — M4: sute de alarme/24h)
    const selLine = _ahLinesCache.find(l => l.id === _ahSelectedLine);
    const nesolvedSel = (selLine?.equipment || []).reduce((n, eq) => {
        const s = _ahSummaryCache[eq.id];
        return n + (s?.unacked_count ?? s?.total_active ?? 0);
    }, 0);
    if (selLine && nesolvedSel > 0) {
        el.innerHTML += `<button type="button" class="btn btn-sm btn-outline" id="ah-ack-line" style="margin-left:auto" title="Confirmă toate alarmele active de pe ${escHtml(selLine.id)}">✔ Confirmă toate pe ${escHtml(selLine.id)} (${nesolvedSel})</button>`;
    }
    el.querySelectorAll('.ah-line-tab').forEach(btn => {
        btn.addEventListener('click', () => {
            _ahSelectedLine = (btn as HTMLElement).dataset.line || '';
            _ahUserChose = true;
            _ahSelectedMachine = '';
            void loadAlarmHistory(1);
        });
    });
    document.getElementById('ah-ack-line')?.addEventListener('click', async () => {
        if (!selLine) return;
        const ok = await customConfirm(
            `Confirmi toate cele ${nesolvedSel} alarme active de pe ${selLine.id}?\nRămân în istoric, doar se marchează văzute.`,
            'Confirmare alarme în bloc');
        if (!ok) return;
        try {
            const r = await fetch('/api/alarms/ack_all', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                // exact utilajele numărate pe buton — nu tot ce are linia în coloana `line` (și răcitoarele partajate)
                body: JSON.stringify({ line_id: selLine.id, machine_ids: (selLine.equipment || []).map(eq => eq.id),
                    acked_by: state.currentUser?.username || 'inginer' }),
            });
            const d = await r.json();
            toast(`✔ ${d.confirmate ?? ''} alarme confirmate pe ${selLine.id}`);
            void loadAlarmHistory(1);
        } catch (e) { toast('Eroare la confirmare', 'error'); }
    });
}

function _renderAhMachineChips(): void {
    const el = document.getElementById('ah-machine-chips');
    if (!el) return;
    const pl = _ahLinesCache.find(l => l.id === _ahSelectedLine);
    if (!pl) {
        el.innerHTML = '';
        return;
    }
    const allActive = (pl.equipment || []).reduce((n, eq) => {
        const s = _ahSummaryCache[eq.id];
        return n + (s?.unacked_count ?? s?.total_active ?? 0);
    }, 0);
    let html = `<button type="button" class="ah-machine-chip${!_ahSelectedMachine ? ' ah-chip-active' : ''}" data-mid="">
        Toate${allActive > 0 ? `<span class="ah-tab-badge">${allActive}</span>` : ''}
    </button>`;
    (pl.equipment || []).forEach(eq => {
        const lbl = (eq as any).display_name || eq.name || eq.id;
        const s = _ahSummaryCache[eq.id];
        const cnt = s?.unacked_count ?? s?.total_active ?? 0;
        const badge = cnt > 0 ? `<span class="ah-tab-badge">${cnt}</span>` : '';
        const active = eq.id === _ahSelectedMachine ? ' ah-chip-active' : '';
        html += `<button type="button" class="ah-machine-chip${active}" data-mid="${escHtml(eq.id)}" title="${escHtml(eq.id)}">
            ${escHtml(lbl)}${badge}
        </button>`;
    });
    el.innerHTML = html;
    el.querySelectorAll('.ah-machine-chip').forEach(btn => {
        btn.addEventListener('click', () => {
            _ahSelectedMachine = (btn as HTMLElement).dataset.mid || '';
            void loadAlarmHistory(1);
        });
    });
}

function _renderAhAlarmCards(alarms: any[]): string {
    const groups: { key: string; label: string; items: any[] }[] = [
        { key: 'active', label: '🔴 Active', items: [] },
        // 11 oct 2026: mașina CERE mentenanță („Întreținere programată 180H”, Sumitomo 7425) — nu e defect; cardul
        // spune ce e la noi pe aceeași cadență (făcută → apasă Start/Stop pe HMI / restantă / mai are N h)
        { key: 'mentenanta', label: '🔧 Mentenanță cerută de mașină', items: [] },
        { key: 'resolved', label: '✅ Rezolvate recent', items: [] },
        { key: 'info', label: 'ℹ️ Info / programate', items: [] },
    ];
    alarms.forEach(a => {
        if (a.active && a.e_mentenanta) groups[1].items.push(a);
        else if (a.active) groups[0].items.push(a);
        else if (a.severity === 'info') groups[3].items.push(a);
        else groups[2].items.push(a);
    });
    let html = '';
    groups.forEach(g => {
        if (!g.items.length) return;
        /* „🔴 Active" se deschide singur — dar când nu există nicio alarmă activă
           (de pildă după un filtru pe „critical", unde toate sunt încheiate),
           ecranul rămânea cu două rânduri strânse și părea gol: rezultatele erau
           acolo, doar că închise. Dacă primul grup n-are nimic, le deschidem pe
           celelalte, ca filtrul să arate ce a găsit. */
        const golPrimul = groups[0].items.length === 0;
        html += `<details class="ah-group"${g.key === 'active' || g.key === 'mentenanta' || golPrimul ? ' open' : ''}>`;
        html += `<summary class="ah-group-summary">${g.label} <span class="ah-group-count">(${g.items.length})</span></summary>`;
        html += '<div class="ah-card-list">';
        // 8 aug 2026 (audit UX): pe M4, 409 din 481 de alarme/24h erau 5 coduri
        // SmartPack repetate — lista devenea nefolosibilă și inginerul confirma
        // în masă fără să vadă ce e nou. Repetările aceluiași cod pe aceeași
        // mașină se strâng într-un singur card, cu numărul și intervalul de timp;
        // codurile care apar o dată rămân exact cum erau.
        const _repet = new Map<string, any[]>();
        g.items.forEach(a => {
            const k = `${a.machine_id}|${a.alarm_code}`;
            if (!_repet.has(k)) _repet.set(k, []);
            _repet.get(k)!.push(a);
        });
        const _afisate: any[] = [];
        // Când serverul a grupat deja (câmpul `aparitii`), luăm numărul lui —
        // e pe tot istoricul, nu doar pe pagina curentă.
        g.items.forEach(a => {
            if (a.aparitii && a.aparitii > 1) {
                a._rep = a.aparitii;
                a._primaLa = a.prima_aparitie;
                a._ultimaLa = a.created_at;
                a._grup = g.label;
                a._ackIds = (a.active && !a.acked) ? [a.id] : [];
            }
        });
        _repet.forEach(lista => {
            if (lista.length > 1) {
                const ts = lista.map(x => x.created_at).filter(Boolean).sort();
                /* Cardul strâns ține minte TOATE alarmele pe care le reprezintă.
                   Fără asta, butonul ACK purta doar `lista[0].id`: inginerul apăsa
                   o dată pe un card care spune „×40 repetări", se confirma UNA, iar
                   cardul se afișa apoi ca „ACK …" — adică 39 de alarme rămâneau
                   neconfirmate sub o etichetă care spunea contrariul. */
                _afisate.push({ ...lista[0], _rep: lista.length,
                                _ackIds: lista.filter(x => x.active && !x.acked).map(x => x.id),
                                _grup: g.label,
                                _primaLa: ts[0], _ultimaLa: ts[ts.length - 1] });
            } else {
                _afisate.push(lista[0]);
            }
        });
        _afisate.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
        _afisate.forEach(a => {
            const sevClass = a.severity || 'info';
            // 11 oct 2026: ora din bază e UTC (și la IMM 8, de azi) — cardul o arăta netradusă, cu 3 ore în urmă
            const timeStr = a.created_at ? formatDateTimeRO(a.created_at) : '';
            const desc = (a.description_ro || a.alarm_code || '—').substring(0, 120);
            const nota = _notaInchidere(a);
            html += `<div class="ah-card ah-row-clickable ${a.active ? 'ah-card-active' : ''}"
                data-machine-id="${escHtml(a.machine_id)}" data-alarm-code="${escHtml(a.alarm_code)}"
                data-alarm-type="${escHtml(a.alarm_type || '')}" data-severity="${escHtml(a.severity || '')}"
                ${a.created_at ? `data-created="${escHtml(a.created_at)}"` : ''} ${a.ora_masina ? `data-ora-masina="${escHtml(a.ora_masina)}"` : ''}
                data-stare="${escHtml(a.active && !a.e_mentenanta ? 'Activă la mașină' : (nota || (a.active ? 'Activă la mașină' : 'Închisă')))}">
                <div class="ah-card-top">
                    <span class="alarm-severity ${sevClass}">${(a.severity || 'info').toUpperCase()}</span>
                    <span class="ah-card-time">${escHtml(timeStr)}</span>
                    <span class="ah-card-code">${escHtml(a.alarm_code || '')}</span>
                    ${a._rep ? `<span class="ah-rep-badge" title="Același cod, repetat de ${a._rep} ori în «${escHtml(a._grup || '')}», între ${escHtml(formatDateTimeRO(a._primaLa))} și ${escHtml(formatDateTimeRO(a._ultimaLa))}">×${a._rep} repetări</span>` : ''}
                </div>
                <div class="ah-card-machine">${escHtml(a.machine_name || a.machine_id)}</div>
                <div class="ah-card-desc">${escHtml(desc)}</div>
                ${nota ? `<div class="ah-card-nota${a.active && a.e_mentenanta ? ' la-noi' : ''}">${escHtml(nota)}</div>` : ''}
                <div class="ah-card-actions">
                    ${(() => {
                        /* Un singur atribut, mereu listă: cardul poate reprezenta
                           o alarmă sau patruzeci, iar butonul trebuie să le
                           confirme pe toate câte le arată. Două atribute ar
                           însemna două adevăruri și exact greșeala reparată aici. */
                        const ids = a._ackIds || ((a.active && !a.acked) ? [a.id] : []);
                        if (!ids.length) return '';
                        /* 19 sep 2026: când gruparea a făcut-o SERVERUL (`?grupat=1`),
                           rândul poartă un singur id — `MAX(a.id)` — deși reprezintă
                           zeci de apariții (M4, codul 331481088: 68). Butonul confirma
                           acel unic rând, restul rămâneau neconfirmate și cardul cerea
                           iar ACK. Atunci trimitem CODUL, iar serverul confirmă grupul
                           întreg, într-o singură scriere. */
                        const grupServer = (a.aparitii || 0) > 1 && ids.length === 1;
                        const nr = grupServer ? (a.neconfirmate || a.aparitii) : ids.length;
                        const eticheta = nr > 1 ? `ACK ×${nr}` : 'ACK';
                        const atrGrup = grupServer
                            ? ` data-masina="${escHtml(a.machine_id || '')}" data-cod="${escHtml(a.alarm_code || '')}"`
                            : '';
                        return `<button type="button" class="btn btn-sm ah-ack-btn" data-ids="${ids.join(',')}"${atrGrup}>${eticheta}</button>`;
                    })()}
                    ${a.acked ? `<span class="ah-acked-badge">ACK ${escHtml(a.acked_by || '')}</span>` : ''}
                </div>
            </div>`;
        });
        html += '</div></details>';
    });
    return html || '<div class="alarm-empty"><span class="icon">✅</span>Nicio alarmă pentru selecția curentă</div>';
}

/** De ce s-a închis alarma (11 oct 2026, Dan): cele mai multe le stinge mașina singură (dop blocat, tub gol);
 * câteva le închidem noi, când mașina n-a mai putut spune — PLC mut / utilaj oprit (`sursa_muta`, scripts/
 * alarme_utilaje_mute.py) sau, la EM63, aceeași alarmă a pornit din nou fără ca resetarea să ajungă la noi
 * (`reactivata`). Cardul strâns de server poartă numărătorile, nu motivul unui singur rând. */
/** cardul unei alarme pe pagina de alarme: rândul de la /api/alarms + ce adaugă gruparea (routes/other.py, `grouped=1`) */
interface CardAlarmaNota extends Pick<Alarm, 'active' | 'e_mentenanta' | 'la_noi' | 'motiv_inchidere' | 'resolved_at'> {
    aparitii?: number; inchise_fara_semnal?: number; resetari_nevazute?: number;
}
function _notaInchidere(a: CardAlarmaNota): string {
    if (a.active && a.e_mentenanta && a.la_noi) return a.la_noi.text;
    if (a.active) return '';
    if ((a.aparitii || 0) > 1) {
        const muta = a.inchise_fara_semnal || 0, reactivate = a.resetari_nevazute || 0;
        const stinse = a.aparitii! - muta - reactivate;   // aici aparitii > 1 (condiția de mai sus)
        if (!muta && !reactivate) return 'Stinse la mașină';
        const parti: string[] = [];
        if (stinse > 0) parti.push(stinse === 1 ? '1 stinsă la mașină' : `${stinse} stinse la mașină`);
        if (reactivate) parti.push(reactivate === 1 ? '1 resetată la mașină fără ca resetarea să ajungă la noi'
            : `${reactivate} resetate la mașină fără ca resetarea să ajungă la noi`);
        // fără oră aici: `resolved_at` al grupului e MAX peste toate rândurile, nu neapărat al celui închis de noi
        if (muta) parti.push(`${muta === 1 ? '1 închisă' : muta + ' închise'} automat: utilajul nu mai raporta`);
        return parti.join(' · ');
    }
    if (a.motiv_inchidere === 'sursa_muta') {
        return `Închisă automat: utilajul nu mai raportează din ${formatDateTimeRO(a.resolved_at)} — starea de la mașină nu se știe`;
    }
    if (a.motiv_inchidere === 'reactivata') {
        return `Resetată la mașină (resetarea n-a ajuns la noi), cel târziu ${formatDateTimeRO(a.resolved_at)}`;
    }
    return a.resolved_at ? `Stinsă la mașină · ${formatDateTimeRO(a.resolved_at)}` : '';
}

function _wireAhCardEvents(container: HTMLElement): void {
    container.querySelectorAll('.ah-ack-btn').forEach(btn => {
        btn.addEventListener('click', async (ev) => {
            ev.stopPropagation();
            const el = btn as HTMLElement;
            // Un card strâns reprezintă mai multe alarme: le confirmăm pe toate.
            const ids = (el.dataset.ids || el.dataset.id || '')
                .split(',').map(x => x.trim()).filter(Boolean);
            if (!ids.length) return;
            el.setAttribute('disabled', 'disabled');
            const cine = state.currentUser?.username || 'operator';
            const masinaGrup = el.dataset.masina || '';
            const codGrup = el.dataset.cod || '';
            if (masinaGrup && codGrup) {
                // Card strâns de server: confirmăm TOT grupul codului, nu doar
                // rândul reprezentativ (vezi nota de la randare).
                const r = await fetch('/api/alarms/ack_all', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ machine_id: masinaGrup, alarm_code: codGrup, acked_by: cine }),
                });
                const d: { ok?: boolean; confirmate?: number } = await r.json().catch(() => ({}));
                if (!r.ok || d.ok !== true) {
                    el.removeAttribute('disabled');
                    toast('Confirmarea nu a ajuns la server — încearcă din nou', 'error');
                    return;
                }
                toast(`✔ ${d.confirmate ?? 1} alarme confirmate`);
            } else if (ids.length === 1) {
                /* 26 aug 2026: aici nu se citea deloc răspunsul serverului — nici
                   `r.ok`, nici corpul. Dacă serverul refuza (403) sau cădea (500),
                   operatorul nu vedea nimic: cardul se reîncărca la fel de
                   neconfirmat și el credea că n-a nimerit butonul. Pe traseul în
                   BLOC exista deja mesaj de confirmare, deci același gest spunea
                   două lucruri diferite. (Efect secundar util: un corp de răspuns
                   necitit ține cererea deschisă în browser — proba cap-coadă
                   aștepta 60 de secunde după el.) */
                const r = await fetch(`/api/alarms/${ids[0]}/ack`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ acked_by: cine }),
                });
                const d: { ok?: boolean } = await r.json().catch(() => ({}));
                if (!r.ok || d.ok !== true) {
                    el.removeAttribute('disabled');
                    toast('Confirmarea nu a ajuns la server — încearcă din nou', 'error');
                    return;
                }
                toast('✔ Alarmă confirmată');
            } else {
                /* În BLOC, nu una câte una: un card strâns poate reprezenta zeci de
                   alarme, iar zeci de cereri paralele se așază la coadă pe zăvorul
                   de scriere al bazei — măsurat, peste un minut până la ultima. */
                const r = await fetch('/api/alarms/ack_all', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ids, acked_by: cine }),
                });
                const d = await r.json().catch(() => ({}));
                toast(`✔ ${d.confirmate ?? ids.length} alarme confirmate`);
            }
            void loadAlarmHistory();
        });
    });
    container.querySelectorAll('.ah-row-clickable').forEach(row => {
        row.addEventListener('click', (ev) => {
            if ((ev.target as HTMLElement).closest('.ah-ack-btn')) return;
            const el = row as HTMLElement;
            const machineId = el.dataset.machineId || '';
            const alarmCode = el.dataset.alarmCode || '';
            if (!machineId || !alarmCode) return;
            openControllerAlarmDetailModal({
                machine_id: machineId,
                machine_name: el.querySelector('.ah-card-machine')?.textContent?.trim() || machineId,
                alarm_code: alarmCode,
                alarm_type: el.dataset.alarmType || undefined,
                severity: el.dataset.severity || 'warning',
                action: el.querySelector('.ah-card-desc')?.textContent?.trim() || undefined,
                created_at: el.dataset.created || undefined,
                ora_masina: el.dataset.oraMasina || undefined,
                stare: el.dataset.stare || undefined,
                title: '🔔 Istoric alarmă',
                showNavButtons: true,
                onHistory: () => loadAlarmHistory(_ahPage),
                onSensors: () => {
                    (window as any).detailTabActive = 'sensors';
                    if ((window as any).openLineDetail) (window as any).openLineDetail(machineId);
                },
                onMachine: () => {
                    (window as any).detailTabActive = 'parts';
                    if ((window as any).openLineDetail) (window as any).openLineDetail(machineId, '', state.currentUser?.role || 'operator');
                },
            });
        });
    });
}

interface AlarmaDeasa {
    machine_id: string;
    machine_name: string;
    alarm_code: string;
    severity: string;
    description: string | null;
    n7: number;
    medie: number | null;
    tendinta: 'creste' | 'mai_des' | 'scade' | 'la_fel' | 'nou';
    durata_min: number;
    important: boolean;
    sugestie: string;
    manual: { solutie?: string | null; pdf_url?: string | null; pagina?: number | null; cod_manual?: string | null } | null;
    /** „Am rezolvat cauza” din ultimele 7 zile: ce s-a făcut și ce a urmat */
    interventie: InterventieAlarma | null;
    istoric_interventii: InterventieAlarma[];
}

interface InterventieAlarma {
    id: number;
    zi: string;
    fel_text: string;
    descriere: string | null;
    facut_de: string | null;
    log_id: number | null;
    n_dupa: number;
    verdict: 'se_masoara' | 'a_ajutat' | 'partial' | 'a_revenit';
}

const _VERDICT_ALARMA: Record<string, string> = {
    se_masoara: '⏱ se măsoară', a_ajutat: '✓ a ajutat', partial: '~ parțial', a_revenit: '↺ a revenit',
};

/** „Am rezolvat cauza” (Dan, 11 oct 2026): ce s-a făcut, apoi fereastra din jurnal, completată și legată de semn.
 * Alarmele nu se șterg; de la semn, „Prea dese” numără din nou și spune dacă a ajutat. */
function _deschideAmRezolvat(machineId: string, cod: string, descriere: string, dupa: () => void): void {
    document.getElementById('modal-am-rezolvat')?.remove();
    const m = document.createElement('div');
    m.id = 'modal-am-rezolvat';
    m.className = 'golive-modal';
    const optiuni: [string, string][] = [['filtru', 'Filtru curățat'], ['reglaj', 'Reglaj'],
        ['piesa', 'Piesă schimbată'], ['altceva', 'Altceva']];
    m.innerHTML = `
        <div class="golive-modal-backdrop"></div>
        <div class="golive-modal-content alert-detail-modal">
            <h2>✔ Am rezolvat cauza</h2>
            <p class="golive-modal-subtitle">${escHtml(cod)} · ${escHtml(descriere)}</p>
            <div class="ah-rezolvat-fel" role="radiogroup" aria-label="Ce ai făcut">
                ${optiuni.map(([v, t], i) => `<label class="ah-rezolvat-opt"><input type="radio" name="ah-rezolvat-fel" value="${v}"${i === 0 ? ' checked' : ''}> ${t}</label>`).join('')}
            </div>
            <textarea id="ah-rezolvat-text" class="dx-input" rows="3" maxlength="500"
                placeholder="Pe scurt, ce ai găsit și ce ai făcut (ex: filtrul de aer de la tub era colmatat)"></textarea>
            <p class="ah-rezolvat-nota">Alarmele vechi rămân în evidență. De acum se numără din nou și vezi dacă a ajutat. Urmează lucrarea în jurnal, deja completată.</p>
            <div class="alert-detail-actions">
                <button type="button" class="btn btn-green" id="ah-rezolvat-salveaza">Salvează și scrie în jurnal</button>
                <button type="button" class="btn btn-outline" id="ah-rezolvat-renunta">Renunță</button>
            </div>
        </div>`;
    document.body.appendChild(m);
    const inchide = () => m.remove();
    m.querySelector('.golive-modal-backdrop')?.addEventListener('click', inchide);
    document.getElementById('ah-rezolvat-renunta')?.addEventListener('click', inchide);
    document.getElementById('ah-rezolvat-salveaza')?.addEventListener('click', async () => {
        const btn = document.getElementById('ah-rezolvat-salveaza') as HTMLButtonElement;
        const fel = (m.querySelector('input[name="ah-rezolvat-fel"]:checked') as HTMLInputElement | null)?.value || 'altceva';
        const text = (document.getElementById('ah-rezolvat-text') as HTMLTextAreaElement | null)?.value.trim() || '';
        btn.disabled = true;
        const r = await fetch('/api/alarme/prea_dese/interventie', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ machine_id: machineId, alarm_code: cod, fel, descriere: text }),
        });
        const d: { ok?: boolean; error?: string; id?: number; text_jurnal?: string } = await r.json().catch(() => ({}));
        if (!r.ok || !d.ok) {
            btn.disabled = false;
            toast(d.error || 'Semnul nu a ajuns la server — încearcă din nou', 'error');
            return;
        }
        inchide();
        toast('✔ Intervenție notată — de acum se numără din nou');
        dupa();
        // fereastra „Am făcut” din jurnal, legată de semn; fără revizia rămasă deschisă din calendar (interval/categorie)
        state.currentRevision = null;
        state.revisionDetailOpenedAt = null;
        (window as Window & { openModal?: typeof import('./tasks-calendar').openModal }).openModal?.({ dataset: { machine: machineId, action: d.text_jurnal, alarmadeasa: String(d.id) } });
    });
}

async function _anuleazaSemnAlarma(id: number, dupa: () => void): Promise<void> {
    if (!confirm('Anulezi semnul „Am rezolvat cauza”? Alarma se numără din nou ca înainte (semnul rămâne în istoric, anulat).')) return;
    const r = await fetch(`/api/alarme/prea_dese/interventie/${id}/anuleaza`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
    });
    const d: { ok?: boolean; error?: string } = await r.json().catch(() => ({}));
    if (!r.ok || !d.ok) {
        toast(d.error || 'Anularea nu a ajuns la server', 'error');
        return;
    }
    toast('Semn anulat');
    dupa();
}

/** „Prea dese” (Dan, 11 oct 2026): alarmele care se sting singure la mașină nu mai rămân active, dar se ține
 * evidența lor — aici cele care revin prea des în ultimele 7 zile, față de media pe 4 săptămâni, cu sugestia din
 * manualul utilajului (services/alarme_dese.py). Doar avertizări și critice; „info” sunt stări de lucru. */
async function _incarcaPreaDese(machineIds: string[]): Promise<void> {
    const el = document.getElementById('ah-dese');
    if (!el) return;
    let lista: AlarmaDeasa[] = [];
    try {
        const q = machineIds.length ? `?machine_ids=${encodeURIComponent(machineIds.join(','))}` : '';
        const data = await api(`/api/alarme/prea_dese${q}`);
        lista = (data && data.alarme) || [];
    } catch {
        el.innerHTML = '';
        return;
    }
    if (!lista.length) {
        el.innerHTML = '';
        return;
    }
    const tend: Record<string, string> = {
        creste: '↑ crește', mai_des: '↗ mai des', scade: '↓ scade', la_fel: '→ ca de obicei', nou: 'istoric scurt',
    };
    const importante = lista.filter(x => x.important).length;
    // deschisă și cât o intervenție e în măsurare: cine tocmai a apăsat „Am rezolvat cauza” vrea să-și vadă cardul
    const deschisa = importante || lista.some(x => x.interventie);
    let html = `<details class="ah-group ah-dese"${deschisa ? ' open' : ''}>
        <summary class="ah-group-summary">↻ Prea dese — ultimele 7 zile <span class="ah-group-count">(${lista.length}${importante ? `, ${importante} de urmărit` : ''})</span></summary>
        <div class="ah-card-list">`;
    const eu = state.currentUser?.username || '';
    const inginer = ['engineer', 'master'].includes(String(state.currentUser?.role || ''));
    for (const x of lista) {
        const manual = x.manual && x.manual.pdf_url
            ? `<a class="btn btn-sm btn-outline" href="${escHtml(x.manual.pdf_url)}" target="_blank" rel="noopener">📖 Manual${x.manual.pagina ? ` p.${x.manual.pagina}` : ''}</a>`
            : '';
        const iv = x.interventie;
        const pasi = x.manual && x.manual.solutie
            ? `<div class="ah-dese-pasi"><strong>Ce spune manualul să faci:</strong> ${escHtml(x.manual.solutie)}</div>`
            : '';
        const anulare = iv && (iv.facut_de === eu || inginer)
            ? `<button type="button" class="btn btn-sm btn-outline ah-dese-anuleaza" data-iv="${iv.id}" title="Semn pus din greșeală">Anulează semnul</button>`
            : '';
        html += `<div class="ah-card ah-dese-card${x.important ? ' ah-dese-important' : ''}">
            <div class="ah-card-top">
                <span class="alarm-severity ${escHtml(x.severity || 'warning')}">${escHtml((x.severity || 'warning').toUpperCase())}</span>
                <span class="ah-card-code">${escHtml(x.alarm_code)}</span>
                <span class="ah-dese-nr">${iv ? `${iv.n_dupa} de la intervenție` : `${x.n7} în 7 zile`}</span>
                ${iv ? `<span class="ah-dese-verdict ${escHtml(iv.verdict)}">${escHtml(_VERDICT_ALARMA[iv.verdict] || '')}</span>`
                     : `<span class="ah-dese-tendinta ${escHtml(x.tendinta)}">${escHtml(tend[x.tendinta] || '')}</span>`}
            </div>
            <div class="ah-card-machine">${escHtml(x.machine_name)}</div>
            <div class="ah-card-desc">${escHtml(x.description || 'fără descriere de la mașină')}</div>
            <div class="ah-dese-sugestie">${escHtml(x.sugestie)}${iv && !iv.log_id ? ' <span class="ah-dese-fara-jurnal">(fără lucrare în jurnal)</span>' : ''}</div>
            ${pasi}
            <div class="ah-card-actions">
                ${iv && iv.verdict !== 'a_revenit' ? '' : `<button type="button" class="btn btn-sm btn-green ah-dese-rezolvat" data-masina="${escHtml(x.machine_id)}" data-cod="${escHtml(x.alarm_code)}" data-desc="${escHtml(x.description || '')}">✔ Am rezolvat cauza</button>`}
                ${manual}
                <button type="button" class="btn btn-sm btn-outline ah-dese-vezi" data-masina="${escHtml(x.machine_id)}" data-cod="${escHtml(x.alarm_code)}">Vezi aparițiile</button>
                ${anulare}
            </div>
        </div>`;
    }
    html += '</div></details>';
    el.innerHTML = html;
    const reincarca = () => { void _incarcaPreaDese(machineIds); };
    el.querySelectorAll<HTMLElement>('.ah-dese-rezolvat').forEach(btn => {
        btn.addEventListener('click', () => _deschideAmRezolvat(
            btn.dataset.masina || '', btn.dataset.cod || '', btn.dataset.desc || '', reincarca));
    });
    el.querySelectorAll<HTMLElement>('.ah-dese-anuleaza').forEach(btn => {
        btn.addEventListener('click', () => { void _anuleazaSemnAlarma(parseInt(btn.dataset.iv || '0', 10), reincarca); });
    });
    el.querySelectorAll<HTMLElement>('.ah-dese-vezi').forEach(btn => {
        btn.addEventListener('click', () => {
            // istoricul de mai jos, filtrat pe utilaj + cod
            const q = document.getElementById('ah-q') as HTMLInputElement | null;
            if (q) q.value = btn.dataset.cod || '';
            _ahSelectedMachine = btn.dataset.masina || '';
            void loadAlarmHistory(1).then(() => {
                document.getElementById('alarm-history-table')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
        });
    });
}

export async function loadAlarmHistory(page?: number): Promise<void> {
    if (page) _ahPage = page;
    const sevFilter = document.getElementById('ah-severity-filter') as HTMLSelectElement;
    const dateFrom = document.getElementById('ah-date-from') as HTMLInputElement;
    const dateTo = document.getElementById('ah-date-to') as HTMLInputElement;

    await _ensureAhLines();
    try {
        _ahSummaryCache = await api('/api/alarms/summary') || {};
        if (!_ahUserChose) _ahSelectedLine = _primaLinieCuAlarme();
    } catch (e) {
        _ahSummaryCache = {};
    }
    _renderAhLineTabs();
    _renderAhMachineChips();

    const pl = _ahLinesCache.find(l => l.id === _ahSelectedLine);
    const eqIds = (pl?.equipment || []).map(e => e.id);

    let url = `/api/alarms?limit=${_ahPerPage}&offset=${(_ahPage - 1) * _ahPerPage}&la_noi=1`;
    if (_ahSelectedMachine) {
        url += `&machine_id=${encodeURIComponent(_ahSelectedMachine)}`;
    } else if (eqIds.length) {
        url += `&machine_ids=${encodeURIComponent(eqIds.join(','))}`;
    }
    if (sevFilter?.value) url += `&severity=${encodeURIComponent(sevFilter.value)}`;
    if (dateFrom?.value) url += `&from=${encodeURIComponent(dateFrom.value)}`;
    if (dateTo?.value) url += `&to=${encodeURIComponent(dateTo.value)}`;
    // căutare pe cod/text și filtru de stare (20 aug 2026): cu 603 pagini de istoric,
    // data și severitatea nu ajung ca să găsești o alarmă anume
    const cautare = (document.getElementById('ah-q') as HTMLInputElement | null)?.value?.trim();
    const stare = (document.getElementById('ah-stare-filter') as HTMLSelectElement | null)?.value;
    if (cautare) url += `&q=${encodeURIComponent(cautare)}`;
    if (stare) url += `&stare=${encodeURIComponent(stare)}`;
    /* 6 sep 2026: gruparea din pagină (8 aug) aduna repetările doar în cele 100
       de rânduri încărcate. Pe M4, istoricul are 40.343 de rânduri și două coduri
       SmartPack fac 27.978 din ele — omul răsfoia sute de pagini cu același cod.
       Serverul le adună acum pe TOATE: 40.343 de rânduri devin 68 de coduri. */
    const grupat = (document.getElementById('ah-grupat') as HTMLInputElement | null)?.checked !== false;
    if (grupat) url += '&grupat=1';

    void _incarcaPreaDese(_ahSelectedMachine ? [_ahSelectedMachine] : eqIds);

    const container = document.getElementById('alarm-history-table');
    if (!container) return;

    try {
        const data = await api(url);
        if (!data.alarms || data.alarms.length === 0) {
            container.innerHTML = '<div class="alarm-empty"><span class="icon">✅</span>Nicio alarmă în istoric pentru această selecție</div>';
            document.getElementById('ah-pagination')!.innerHTML = '';
            return;
        }
        container.innerHTML = _renderAhAlarmCards(data.alarms);
        _wireAhCardEvents(container);

        const totalPages = Math.ceil(data.total / _ahPerPage);
        let pagHtml = '';
        if (totalPages > 1) {
            // Cu 603 pagini, două săgeți nu ajung: de pe pagina 500 nu te mai
            // întorci la prima fără să dai clic de 499 de ori (Dan, 20 aug 2026).
            // Acum: prima / anterioara / câmp în care scrii pagina / următoarea / ultima.
            const dez = (cond: boolean) => cond ? '' : ' disabled';
            pagHtml += `<button type="button" class="btn btn-sm ah-page-btn" data-p="1" title="Prima pagină"${dez(_ahPage > 1)}>«</button>`;
            pagHtml += `<button type="button" class="btn btn-sm ah-page-btn" data-p="${_ahPage - 1}" title="Pagina anterioară"${dez(_ahPage > 1)}>‹</button>`;
            pagHtml += `<span class="ah-page-label">Pagina
                <input type="number" id="ah-page-input" class="ah-page-input" min="1" max="${totalPages}"
                       value="${_ahPage}" title="Scrie pagina și apasă Enter" aria-label="Sari la pagina">
                din ${totalPages}</span>`;
            pagHtml += `<button type="button" class="btn btn-sm ah-page-btn" data-p="${_ahPage + 1}" title="Pagina următoare"${dez(_ahPage < totalPages)}>›</button>`;
            pagHtml += `<button type="button" class="btn btn-sm ah-page-btn" data-p="${totalPages}" title="Ultima pagină"${dez(_ahPage < totalPages)}>»</button>`;
        }
        const pagContainer = document.getElementById('ah-pagination')!;
        pagContainer.innerHTML = pagHtml;
        pagContainer.querySelectorAll('.ah-page-btn').forEach(b => {
            b.addEventListener('click', () => loadAlarmHistory(parseInt((b as HTMLElement).dataset.p!, 10)));
        });
        const camp = document.getElementById('ah-page-input') as HTMLInputElement | null;
        const sari = () => {
            const p = Math.max(1, Math.min(totalPages, parseInt(camp!.value, 10) || 1));
            camp!.value = String(p);
            if (p !== _ahPage) loadAlarmHistory(p);
        };
        camp?.addEventListener('keydown', (ev) => {
            if ((ev as KeyboardEvent).key === 'Enter') { ev.preventDefault(); sari(); }
        });
        camp?.addEventListener('change', sari);
    } catch (e) {
        container.innerHTML = '<p style="color:var(--red)">Eroare la încărcarea istoricului</p>';
    }
}

document.getElementById('ah-search-btn')?.addEventListener('click', () => loadAlarmHistory(1));
document.getElementById('ah-q')?.addEventListener('keydown', (ev) => {
    if ((ev as KeyboardEvent).key === 'Enter') { ev.preventDefault(); loadAlarmHistory(1); }
});
document.getElementById('ah-grupat')?.addEventListener('change', () => loadAlarmHistory(1));
document.getElementById('ah-stare-filter')?.addEventListener('change', () => loadAlarmHistory(1));
document.getElementById('ah-severity-filter')?.addEventListener('change', () => loadAlarmHistory(1));
document.getElementById('ah-reset-btn')?.addEventListener('click', () => {
    ['ah-q', 'ah-date-from', 'ah-date-to'].forEach(id => {
        const el = document.getElementById(id) as HTMLInputElement | null;
        if (el) el.value = '';
    });
    ['ah-stare-filter', 'ah-severity-filter'].forEach(id => {
        const el = document.getElementById(id) as HTMLSelectElement | null;
        if (el) el.value = '';
    });
    loadAlarmHistory(1);
});

/* ═══ HELP REQUESTS BADGE ═══ */
export async function _updateHelpBadge(): Promise<void> {
    try {
        const res = await api('/api/help_requests/count');
        const badge = document.getElementById('help-badge');
        if (!badge) return;
        const isMaster = state.currentUser && (state.currentUser.role === 'master' || state.currentUser.role === 'engineer');
        if (res.open > 0 && isMaster) {
            badge.style.display = '';
            badge.textContent = '📞 ' + res.open + ' cereri ajutor';
            badge.className = 'help-badge-top active';
        } else {
            badge.style.display = 'none';
        }
    } catch(e) {}
}
setInterval(_updateHelpBadge, 30000);

document.getElementById('help-badge')?.addEventListener('click', () => {
    _openHelpDashboard();
});

export async function _openHelpDashboard(): Promise<void> {
    const reqs = await api('/api/help_requests?status=open').catch(() => []);
    const responded = await api('/api/help_requests?status=responded').catch(() => []);
    const all = [...(Array.isArray(reqs) ? reqs : []), ...(Array.isArray(responded) ? responded : [])];
    let existing = document.getElementById('help-dashboard-overlay');
    if (existing) existing.remove();

    const isMaster = state.currentUser && (state.currentUser.role === 'master' || state.currentUser.role === 'engineer');

    let listHtml = '';
    if (all.length === 0) {
        listHtml = '<div class="dx-empty" style="margin:20px 0">Nicio cerere de ajutor activă</div>';
    } else {
        all.forEach(r => {
            const statusIcon = r.status === 'open' ? '🔴' : r.status === 'responded' ? '🟢' : '⚪';
            listHtml += '<div class="help-req-card" data-req-id="' + r.id + '">' +
                '<div class="help-req-header">' +
                '<span>' + statusIcon + ' <strong>#' + r.id + '</strong> — ' + escHtml(r.machine_id || '—') + '</span>' +
                '<span style="color:var(--text-muted);font-size:.85rem">' + escHtml(r.requested_at || '').substring(0, 16) + '</span></div>' +
                '<div class="help-req-msg">' + escHtml(r.message || '').replace(/\n/g, '<br>') + '</div>' +
                '<div style="font-size:.85rem;color:var(--text-muted)">De la: <strong>' + escHtml(r.requested_by || '—') + '</strong></div>';
            if (r.response) {
                listHtml += '<div class="help-req-response"><strong>Răspuns (' + escHtml(r.responded_by || '') + '):</strong> ' + escHtml(r.response) + '</div>';
            }
            if (r.status === 'open' && isMaster) {
                listHtml += '<div class="help-req-actions">' +
                    '<textarea class="dx-input help-resp-text" rows="2" placeholder="Scrie instrucțiuni pentru operator..." style="margin:8px 0"></textarea>' +
                    '<button class="dx-btn-primary help-resp-btn" data-req-id="' + r.id + '" style="padding:8px 20px;font-size:.88rem">📨 Trimite răspuns</button></div>';
            }
            listHtml += '</div>';
        });
    }

    const overlay = document.createElement('div');
    overlay.id = 'help-dashboard-overlay';
    overlay.className = 'proc-overlay';
    overlay.innerHTML =
        '<div class="proc-container" style="max-width:650px;overflow-y:auto">' +
        '<div class="proc-header"><div class="proc-header-top">' +
        '<h2 class="proc-title">📞 Cereri de ajutor</h2>' +
        '<button class="proc-close" id="help-dash-close">&times;</button>' +
        '</div></div>' +
        '<div style="padding:16px">' + listHtml + '</div></div>';
    document.body.appendChild(overlay);

    document.getElementById('help-dash-close')!.addEventListener('click', () => overlay.remove());

    (overlay.querySelectorAll('.help-resp-btn') as NodeListOf<HTMLButtonElement>).forEach(btn => {
        btn.addEventListener('click', async () => {
            const reqId = btn.dataset.reqId;
            const textarea = btn.parentElement!.querySelector('.help-resp-text') as HTMLTextAreaElement | null;
            const text = textarea ? textarea.value.trim() : '';
            if (!text) { toast('Scrie un răspuns!', 'warning'); textarea?.focus(); return; }
            btn.disabled = true; btn.textContent = '⏳...';
            try {
                await api('/api/help_requests/' + reqId + '/respond', {
                    method: 'POST', headers: {'Content-Type':'application/json'},
                    body: JSON.stringify({ response: text })
                });
                toast('✅ Răspuns trimis!', 'success');
                _updateHelpBadge();
                _openHelpDashboard();
            } catch(e) { toast('Eroare: ' + (e as Error).message, 'error'); btn.disabled = false; btn.textContent = '📨 Trimite răspuns'; }
        });
    });
}

/* ═══ deschiderea panoului ═════════════════════════════════════════════════
 * `hidden` + `.open` în loc de doar `.open`: panoul e acum un dialog peste ecran,
 * nu o fâșie lipită de margine. Fără `hidden` ar rămâne în pagină, invizibil dar
 * prezent, și ar prinde clicuri peste conținut.
 */
export function deschideAlarme(fila?: 'utilaje' | 'aplicatie', linie?: string | null): void {
    const panou = $('#alarm-panel') as HTMLElement;
    const fundal = $('#alarm-overlay') as HTMLElement;
    if (fila) filaAlarme = fila;
    liniaAlarme = linie || null;     // deschis din meniu = toate liniile
    panou.hidden = false; fundal.hidden = false;
    requestAnimationFrame(() => panou.classList.add('open'));
    void Promise.all([incarcaIncidenteAplicatie(), incarcaAbateri()]).then(() => {
        actualizeazaButonAlarme();
        renderAlarmPanel();
    });
    renderAlarmPanel();
}

export function inchideAlarme(): void {
    const panou = $('#alarm-panel') as HTMLElement;
    const fundal = $('#alarm-overlay') as HTMLElement;
    liniaAlarme = null;              // filtrul pe linie ține doar cât e panoul deschis de pe card
    panou.classList.remove('open');
    setTimeout(() => { panou.hidden = true; fundal.hidden = true; }, 180);
}

function comutaAlarme(): void {
    const panou = $('#alarm-panel') as HTMLElement;
    if (panou.hidden) deschideAlarme(); else inchideAlarme();
}

$('#alarme-buton')?.addEventListener('click', () => comutaAlarme());
$('#alarm-close-btn')!.addEventListener('click', () => inchideAlarme());
$('#alarm-overlay')?.addEventListener('click', () => inchideAlarme());

(document.querySelectorAll('.alarm-fila') as NodeListOf<HTMLElement>).forEach(f => {
    f.addEventListener('click', () => {
        filaAlarme = (f.dataset.fila as 'utilaje' | 'aplicatie') || 'utilaje';
        if (liniaAlarme) {           // schimbarea filei iese din vederea pe o linie (altfel „Aplicație” apare goală)
            liniaAlarme = null;
            void incarcaIncidenteAplicatie().then(() => renderAlarmPanel());
            return;
        }
        renderAlarmPanel();
    });
});

document.addEventListener('keydown', (ev) => {
    const e = ev as KeyboardEvent;
    if (e.key === 'Escape' && !($('#alarm-panel') as HTMLElement).hidden) inchideAlarme();
});
$('#ack-all-btn')!.addEventListener('click', async () => {
    [...state.allAlerts, ...(state.sensorAlarms || [])].forEach(a => {
        if (!state.ackedAlarms.includes(a.id)) state.ackedAlarms.push(a.id);
    });
    localStorage.setItem('ackedAlarms', JSON.stringify(state.ackedAlarms));
    try {
        await fetch('/api/alarms/ack_all', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({acked_by: state.currentUser?.username || 'operator'}),
        });
        state.controllerAlarms!.forEach(a => { a.acked = true; });
    } catch (e) {}
    updateAlarmBadge(0);
    renderAlarmPanel();
    toast('Toate alarmele confirmate');
});

/* ═══ SHARED MACHINE CACHE & LINE UTILITIES ═══════════════════ */
export let _allMachinesCache: Machine[] | null = null;
export let _machineCachePromise: Promise<Machine[]> | null = null;

export async function _ensureMachineCache(): Promise<Machine[]> {
    if (_allMachinesCache) return _allMachinesCache;
    if (!_machineCachePromise) {
        _machineCachePromise = api('/api/machines/all').then(machines => {
            _allMachinesCache = machines;
            _lineMap = _buildLineMap(machines);
            return machines;
        });
    }
    return _machineCachePromise;
}

export const _lineLabels: Record<string, string> = {
    'M1': 'Linia M1', 'M2': 'Linia M2', 'M3': 'Linia M3', 'M4': 'Linia M4',
    'M5': 'Linia M5', 'M6': 'Linia M6', 'M7': 'Linia M7', 'M8': 'Linia M8',
    'M9': 'Linia M9', 'LINIE ROBOT': 'Linia Robot (ambalare)',
    'I28': 'Linia I28 — imprimare 28', 'I48': 'Linia I48 — imprimare 48',
    'UTIL_AER': 'Compresoare & Aer comprimat',
    'TRAFO': 'Transformator General',
    '_racitoare': 'Răcitoare externe (partajate)',
};

/* Liniile de producție se recunosc după tipar (M1…M9, I28, I48), nu după o
   listă scrisă de mână: pe 5 sep 2026 au apărut liniile de imprimare I28/I48 și
   ar fi căzut în grupa «Utilități & Accesorii», iar M9 și linia robotului erau
   deja acolo de luni de zile. O linie nouă intră singură în grupa bună. */
export function _eLinieProductie(cheie: string): boolean {
    return /^[A-Z]{1,3}\s?\d+$/.test(cheie) || cheie === 'LINIE ROBOT';
}

export function _eticheta_linie(cheie: string): string {
    return _lineLabels[cheie] || (_eLinieProductie(cheie) ? `Linia ${cheie}` : cheie);
}

export function _buildLineMap(machines: Machine[]): Record<string, Machine[]> {
    const map: Record<string, Machine[]> = {};
    const shared: Machine[] = [];
    machines.forEach(m => {
        const lineRaw = m.line || '';
        const isShared = lineRaw.includes(',');
        if (isShared) shared.push(m);
        const lines = isShared ? lineRaw.split(',').map(l => l.trim()) : [lineRaw];
        lines.forEach(l => {
            const key = l || '_other';
            if (!map[key]) map[key] = [];
            map[key].push(m);
        });
    });
    if (shared.length) map['_racitoare'] = shared;
    return map;
}

export let _lineMap: Record<string, Machine[]> = {};

export function _updateMachineSelect(lineKey: string): void {
    const sel = $('#jrn-machine') as HTMLSelectElement | null;
    if (!sel) return;
    if (!lineKey) {
        sel.innerHTML = '<option value="">— Selectează mai întâi linia —</option>';
        sel.disabled = true;
        return;
    }
    const machines = _lineMap[lineKey] || [];
    sel.disabled = false;
    sel.innerHTML = '<option value="">— Alege utilajul —</option>';
    machines.forEach(m => {
        const lineSuffix = lineKey === '_racitoare' ? ` [${m.line}]` : '';
        sel.innerHTML += `<option value="${m.id}">${m.id} — ${escHtml(m.name || '')}${lineSuffix}</option>`;
    });
}

export function _populateLineSelect(selectul: Element | null, allLabel?: string, showCount?: boolean): void {
    const lineSel = selectul as HTMLSelectElement | null;
    if (!lineSel || !_lineMap) return;
    lineSel.innerHTML = `<option value="">${allLabel || 'Toate liniile'}</option>`;
    const prod = Object.keys(_lineMap).filter(k => _eLinieProductie(k)).sort();
    const util = Object.keys(_lineMap).filter(k => !_eLinieProductie(k) && k !== '_other' && k !== '_racitoare');
    if (prod.length) {
        lineSel.innerHTML += '<optgroup label="Linii producție">';
        prod.forEach(k => {
            const label = _eticheta_linie(k);
            const cnt = showCount ? ` (${_lineMap[k].length})` : '';
            lineSel.innerHTML += `<option value="${k}">${label}${cnt}</option>`;
        });
        lineSel.innerHTML += '</optgroup>';
    }
    if (_lineMap['_racitoare']?.length) {
        lineSel.innerHTML += '<optgroup label="Răcitoare externe">';
        const cnt = showCount ? ` (${_lineMap['_racitoare'].length})` : '';
        lineSel.innerHTML += `<option value="_racitoare">${_lineLabels['_racitoare']}${cnt}</option>`;
        lineSel.innerHTML += '</optgroup>';
    }
    if (util.length) {
        lineSel.innerHTML += '<optgroup label="Utilități & Accesorii">';
        util.forEach(k => {
            const label = _lineLabels[k] || k;
            const cnt = showCount ? ` (${_lineMap[k].length})` : '';
            lineSel.innerHTML += `<option value="${k}">${label}${cnt}</option>`;
        });
        lineSel.innerHTML += '</optgroup>';
    }
    if (_lineMap['_other']) {
        const cnt = showCount ? ` (${_lineMap['_other'].length})` : '';
        lineSel.innerHTML += `<option value="_other">Alte utilaje nealocate${cnt}</option>`;
    }
}

export function _linkLineToMachineSelect(liniaSel: Element | null, masinaSel: Element | null, allMachinesLabel?: string, disableWhenNoLine?: boolean): void {
    const lineSel = liniaSel as HTMLSelectElement | null, machineSel = masinaSel as HTMLSelectElement | null;
    if (!lineSel || !machineSel) return;
    lineSel.addEventListener('change', () => {
        const lk = lineSel.value;
        if (!lk) {
            if (disableWhenNoLine) {
                machineSel.innerHTML = `<option value="">— Selectează mai întâi linia —</option>`;
                machineSel.disabled = true;
            } else {
                machineSel.innerHTML = `<option value="">${allMachinesLabel || 'Toate mașinile'}</option>`;
                (_allMachinesCache || []).forEach(m => {
                    machineSel.innerHTML += `<option value="${m.id}">${m.id} — ${escHtml(m.name || '')}</option>`;
                });
            }
        } else {
            const machines = _lineMap[lk] || [];
            machineSel.disabled = false;
            machineSel.innerHTML = `<option value="">${allMachinesLabel || 'Toate din linie'}</option>`;
            machines.forEach(m => {
                const suf = lk === '_racitoare' ? ` [${m.line}]` : '';
                machineSel.innerHTML += `<option value="${m.id}">${m.id} — ${escHtml(m.name || '')}${suf}</option>`;
            });
        }
    });
}

export function _insertLineSelectBefore(machineSel: Element | null, lineId: string, allLineLabel?: string, allMachineLabel?: string): HTMLSelectElement | null {
    if (!machineSel) return null;
    let lineSel = document.getElementById(lineId) as HTMLSelectElement | null;
    if (lineSel) return lineSel;
    lineSel = document.createElement('select');
    lineSel.id = lineId;
    lineSel.className = machineSel.className;
    lineSel.style.cssText = (machineSel as HTMLElement).style.cssText;
    /* Numele se punea DOAR dacă select-ul de mașină avea la rândul lui `aria-label`.
       Unde mașina e etichetată printr-un `<label for=...>` vizibil, selectorul de
       linie se strecura ÎNTRE etichetă și câmpul ei: pe ecran scria „Mașină"
       deasupra unui select care alege linia, iar eticheta rămânea legată de
       select-ul de dedesubt. Numele se pune întotdeauna. */
    lineSel.setAttribute('aria-label', 'Linie');
    lineSel.title = 'Filtrează după linie';
    machineSel.parentNode!.insertBefore(lineSel, machineSel);
    /* Eticheta spunea «Mașină», dar primul câmp de sub ea alege LINIA. Dan, 5
       sep 2026, a citit exact ce scria acolo — «Mașină: Linia M1» — și a crezut
       că filtrează utilajul. Textul trebuie să spună ce fac amândouă. */
    const et = machineSel.parentElement;
    if (et && et.tagName === 'LABEL') {
        et.childNodes.forEach(nod => {
            if (nod.nodeType === Node.TEXT_NODE && /Ma[sș]in[ăa]\s*:/.test(nod.textContent || '')) {
                nod.textContent = (nod.textContent || '').replace(/Ma[sș]in[ăa]\s*:/, 'Linie / utilaj:');
            }
        });
    }
    _populateLineSelect(lineSel!, allLineLabel || 'Toate liniile', false);
    _linkLineToMachineSelect(lineSel!, machineSel, allMachineLabel || 'Toate mașinile', false);
    return lineSel;
}

export function setAllMachinesCache(machines: Machine[]): void {
    _allMachinesCache = machines;
    _lineMap = _buildLineMap(machines);
}
